import { useState, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Wind, Thermometer, Droplets, Gauge, CheckCircle2, AlertCircle,
  Info, RotateCcw, FileText, TrendingUp, Zap, FlaskConical,
  ChevronDown, ChevronUp, X, Beaker, BarChart3, Settings,
  Layers, ArrowRight, AlertTriangle, Flame, Activity, Target,
  ShieldCheck, ClipboardList,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { ScienceAccordion } from '@/components/ScienceAccordion';
import type { ScienceSection } from '@/components/ScienceAccordion';
import {
  LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';

/* ─── Ammonia Air Stripping Tower — Specification Page ──────────────────────
   Packed-bed and VTA countercurrent air stripping for NH3/NH4+ removal.
   Fully supports high-temperature hot stripping (80–90°C) via Arrhenius HTU
   correction; two-stage series cascade (3S VTA type per project design basis).
   Industry standards: Metcalf & Eddy 5th Ed.; US EPA/625/1-74/016;
   Staudinger & Roberts (2001); WEF MOP 8.
   ────────────────────────────────────────────────────────────────────────── */

// ── Types ──────────────────────────────────────────────────────────────────

type PackingType = 'pall' | 'raschig' | 'structured' | 'saddles' | 'vta';
type ReagentType = 'NaOH' | 'lime' | 'none';

interface StripperParams {
  // Water quality
  flowRate:      number;   // m³/h per unit
  inletTAN:      number;   // mg/L TAN (total ammonia nitrogen)
  targetTAN:     number;   // mg/L TAN target effluent
  pH:            number;
  temperature:   number;   // °C
  // Feed characteristics (pre-treatment context + fouling checks)
  codFeed:       number;   // mg/L COD
  tdsFeed:       number;   // mg/L TDS
  tssFeed:       number;   // mg/L TSS
  freeOilFeed:   number;   // mg/L free oil
  bodFeed:       number;   // mg/L BOD5
  ureaPpm:       number;   // mg/L urea (upset condition)
  // Feed water characterisation — A-series (scaling / MoC / caustic)
  alkalinityMgL:       number;  // A1 — mg/L as CaCO₃
  totalHardnessMgL:    number;  // A2 — mg/L as CaCO₃
  calciumHardnessMgL:  number;  // A2 — mg/L Ca as CaCO₃
  silicaMgL:           number;  // A3 — mg/L SiO₂
  chlorideMgL:         number;  // A4 — mg/L Cl⁻
  freeCO2MgL:          number;  // A5 — mg/L free CO₂
  upsetNH3Direct:      number;  // A8 — peak NH₃ at inlet (ppm, directly specified)
  // Tower design
  packingType:   PackingType;
  glRatio:       number;   // G/L volumetric air-to-water ratio (m³_air/m³_liq)
  liquidLoading: number;   // m³/m²·h
  safetyFactor:  number;
  // pH correction
  reagent:       ReagentType;
  // Integration
  flarePressureKPa: number; // ammonia flare back-pressure kPa
}

interface StripperResult {
  // NH3/NH4+ equilibrium
  pKa:     number;
  fNH3:    number;
  freeNH3: number;
  nh4conc: number;
  // Urea hydrolysis (high-temperature)
  ureaHydrolysisEff: number;  // 0–1 completeness
  ureaContrib:       number;  // mg/L NH3 contributed by urea hydrolysis
  upsetInletTAN:     number;  // inletTAN + ureaContrib (upset scenario)
  // Henry's law
  hcc:  number;
  hEff: number;
  // Steam content
  waterVapourPressureKPa: number;
  steamContentPct:        number;
  moistureWarning:        boolean;
  // Stripping
  S:   number;
  NTU: number;
  HTU: number;   // m (Arrhenius-corrected)
  // Tower geometry
  Z:               number;  // theoretical height m
  Zdesign:         number;  // design height m
  minHeightApplied: boolean;
  A:               number;  // cross-section m²
  D:               number;  // diameter m
  // Air / blower
  Qair:            number;  // m³/h (per unit)
  staticHead:      number;  // kPa
  blowerPressure:  number;  // kPa total incl. flare back-pressure
  blowerPower:     number;  // kW
  // Performance
  tanOut:   number;   // mg/L estimated effluent
  removal:  number;   // %
  // pH dose
  pHDose:   number;   // g/m³ (base pH adjustment)
  // Refined caustic dose (A1/A5)
  causticDoseAlk:   number;  // g/m³ NaOH to neutralise alkalinity
  causticDoseCO2:   number;  // g/m³ NaOH to neutralise free CO₂
  causticDoseTotal: number;  // g/m³ total NaOH dose
  co2ionised:       number;  // % of CO₂ ionised at operating pH
  // Langelier Saturation Index (D4 — scaling / CaCO₃ precipitation)
  lsi:            number;
  scalingRisk:    'none' | 'low' | 'moderate' | 'high' | 'severe';
  scalingDrivers: string[];
  // Material of Construction (D — MoC rule engine)
  mocCode:      string;
  mocRationale: string;
  // DO
  doIncrease: number; // mg/L estimated
  // Validations
  validations: { label: string; pass: boolean; value: string }[];
}

interface SeriesResult {
  stage1:              StripperResult;
  stage2:              StripperResult;
  overallRemoval:      number;  // %
  totalQairPerStage:   number;  // m³/h per unit
  totalQairAll:        number;  // m³/h all units combined (×unitsPerStage)
  totalBlowerPowerAll: number;  // kW all units
  finalTAN:            number;  // mg/L final effluent
}

// ── Constants ───────────────────────────────────────────────────────────────

const PACKING_HTU20: Record<PackingType, { htu: number; label: string; notes: string }> = {
  pall:       { htu: 1.10, label: 'Plastic Pall rings 50 mm',          notes: 'Industry standard random packing; HTU₂₀ = 1.1 m (Metcalf & Eddy Table 7-24)' },
  raschig:    { htu: 1.40, label: 'Plastic Raschig rings 50 mm',       notes: 'Older random packing; higher HTU than Pall rings' },
  structured: { htu: 0.70, label: 'Structured packing (Mellapak 250Y)', notes: 'Low pressure drop; excellent mass transfer; HTU₂₀ ≈ 0.7 m' },
  saddles:    { htu: 0.90, label: 'Plastic saddles 38 mm',             notes: 'Intalox/Berl saddles; intermediate performance' },
  vta:        { htu: 0.60, label: 'VTA — Venturi Tower Aerator (3S)',   notes: 'Venturi-induced fine-bubble contact; no packing flooding; min tower height 3.0 m for HRT. HTU₂₀ = 0.60 m (conservative estimate; vendor to confirm via pilot data).' },
};

const REAGENT_LABELS: Record<ReagentType, string> = {
  NaOH: 'NaOH (Caustic soda, 50% solution)',
  lime: 'Ca(OH)₂ (Hydrated lime slurry)',
  none: 'None (no pH correction)',
};

// ── Project Design Basis (3S VTA — RCER-2025) ──────────────────────────────

export const PROJECT_DESIGN_BASIS = {
  description:    'RCER-2025 Ammonia Stripper — 3S VTA Two-Stage Configuration',
  regulation:     'Royal Commission Environmental Regulations 2025 (RCER-2025)',
  totalFlow:      135,    // m³/hr
  unitsPerStage:  3,
  unitRatedFlow:  50,     // m³/hr rated per unit
  flowPerUnit:    45,     // m³/hr design (135/3)
  phRange:        [10.5, 11.0] as [number, number],
  tempRange:      [80, 90]    as [number, number],
  // Ammonia performance targets
  inletS1:        6500,   // mg/L TAN after heat exchanger
  outletS1:       780,    // mg/L TAN Stripper 1 outlet (88% removal)
  outletS2:       100,    // mg/L TAN Stripper 2 outlet (final, 87% of S1 out)
  overallTarget:  100,    // mg/L final
  // Feed water quality characteristics
  cod:            250,    // mg/L
  tds:            5000,   // mg/L
  tss:            10,     // mg/L
  freeOil:        5,      // mg/L
  bod:            125,    // mg/L
  // Upset conditions
  upsetNH3ppm:    10000,  // ppm (1 wt%) NH3 peak
  upsetUreaPpm:   1000,   // ppm urea
  // Integration
  flareBackPressure: 5.0, // kPa (typical ammonia flare header, range 3–8 kPa)
  // Pre-treatment train
  pretrain:       ['CPI', 'DNF', 'Heat Exchanger', 'Stripper 1', 'Stripper 2'],
  // Incoming streams to WWTP
  incomingStreams: [
    'First Flush', 'PCCC blowdown', 'DM regeneration',
    'CO₂ compressor condensate', 'Oily water',
  ],
};

// ── Default Params ───────────────────────────────────────────────────────────

const DEFAULT_PARAMS: StripperParams = {
  flowRate:         100,
  inletTAN:         50,
  targetTAN:        5,
  pH:               10.5,
  temperature:      15,
  codFeed:          0,
  tdsFeed:          0,
  tssFeed:          0,
  freeOilFeed:      0,
  bodFeed:          0,
  ureaPpm:          0,
  alkalinityMgL:       200,
  totalHardnessMgL:    300,
  calciumHardnessMgL:  200,
  silicaMgL:           20,
  chlorideMgL:         200,
  freeCO2MgL:          10,
  upsetNH3Direct:      0,
  packingType:      'pall',
  glRatio:          200,
  liquidLoading:    15,
  safetyFactor:     1.2,
  reagent:          'NaOH',
  flarePressureKPa: 0,
};

// VTA 3S project pre-set (per project design basis)
const VTA_PROJECT_PARAMS: StripperParams = {
  flowRate:         45,     // per unit (135/3)
  inletTAN:         6500,
  targetTAN:        780,    // stage 1 target; stage 2 = 100
  pH:               10.75,  // mid-point 10.5–11.0
  temperature:      85,     // mid-point 80–90°C
  codFeed:          250,
  tdsFeed:          5000,
  tssFeed:          10,
  freeOilFeed:      5,
  bodFeed:          125,
  ureaPpm:          1000,   // upset condition
  alkalinityMgL:       500,   // A1 — high alkalinity from process water
  totalHardnessMgL:    800,   // A2 — combined hardness
  calciumHardnessMgL:  500,   // A2 — Ca hardness (as CaCO₃)
  silicaMgL:           30,    // A3 — SiO₂ (scaling precursor above 50 mg/L at pH 11)
  chlorideMgL:         800,   // A4 — Cl⁻ drives MoC selection
  freeCO2MgL:          20,    // A5 — free CO₂ adds to caustic demand during pH adjustment
  upsetNH3Direct:      10000, // A8 — peak NH₃ at inlet (ppm, 1 wt%)
  packingType:      'vta',
  glRatio:          300,
  liquidLoading:    20,
  safetyFactor:     1.2,
  reagent:          'NaOH',
  flarePressureKPa: 5.0,
};

// ── Core Calculation ────────────────────────────────────────────────────────

function calcStripper(p: StripperParams): StripperResult {
  const T_K = p.temperature + 273.15;

  // ── 0. Urea hydrolysis at elevated temperature ──────────────────────────
  // Reaction: CO(NH₂)₂ + H₂O → CO₂ + 2 NH₃  (MW: urea=60, NH₃=17)
  // First-order rate k increases steeply with T; essentially complete at ≥80°C.
  // Efficiency: 0% below 40°C, linear 40→80°C, 100% at ≥80°C.
  const ureaHydrolysisEff = p.temperature >= 80 ? 1.0
    : p.temperature >= 40 ? (p.temperature - 40) / 40
    : 0;
  const ureaContrib    = p.ureaPpm * (2 * 17 / 60) * ureaHydrolysisEff; // mg/L NH₃
  const upsetInletTAN  = p.inletTAN + ureaContrib;

  // ── 1. NH₃/NH₄⁺ equilibrium (Henderson–Hasselbalch) ───────────────────
  // pKa(T) from Bates & Pinching (1949), adopted by EPA Design Manual:
  //   pKa(T) = 0.09018 + 2729.92 / T_K
  const pKa   = 0.09018 + 2729.92 / T_K;
  const fNH3  = 1 / (1 + Math.pow(10, pKa - p.pH));  // free NH₃ fraction
  const freeNH3 = p.inletTAN * fNH3;
  const nh4conc = p.inletTAN * (1 - fNH3);

  // ── 2. Henry's constant (Staudinger & Roberts, Chemosphere 44, 2001) ───
  // Dimensionless (concentration basis, mol/m³_gas ÷ mol/m³_liq):
  //   Hcc(T) = 0.01144 × exp[4092 × (1/293.15 − 1/T_K)]
  // Valid ~5–95°C; uncertainty ±15% at T > 70°C.
  // TDS salting-out correction (Setchenow, 1889):
  //   H_salt ≈ H_pure × 10^(Ks × Cs)  where Ks≈0.1 L/mol, Cs=ionic strength
  // At TDS=5000 mg/L: correction ≈ +2% (minor; included for completeness)
  const tdsMolality = (p.tdsFeed > 0 ? p.tdsFeed : 0) / (58440); // ≈NaCl equivalent
  const setchenowCorr = Math.pow(10, 0.1 * tdsMolality);
  const hcc  = 0.01144 * Math.exp(4092 * (1 / 293.15 - 1 / T_K)) * setchenowCorr;
  const hEff = hcc * fNH3;

  // ── 3. Stripping factor ─────────────────────────────────────────────────
  // S = H_eff × (G/L)  — dimensionless;  S > 1 required for net transfer to gas
  const S = hEff * p.glRatio;

  // ── 4. NTU — countercurrent packed tower (Strigle, 1994) ───────────────
  // For S ≠ 1:  NTU = [S/(S−1)] × ln[(Cin/Cout)×(1−1/S) + 1/S]
  // For S = 1:  NTU = ln(Cin/Cout)  (log-mean approximation)
  let NTU = 0;
  if (S > 1.001 && p.inletTAN > 0 && p.targetTAN > 0 && p.inletTAN > p.targetTAN) {
    const ratio = p.inletTAN / p.targetTAN;
    NTU = (S / (S - 1)) * Math.log(ratio * (1 - 1 / S) + 1 / S);
  } else if (S <= 1.001 && p.inletTAN > 0 && p.targetTAN > 0) {
    NTU = Math.log(p.inletTAN / p.targetTAN);
  }
  NTU = Math.max(0, NTU);

  // ── 5. HTU — Arrhenius temperature correction ───────────────────────────
  // Replaces previous linear approximation (invalid above ~50°C).
  // Per Metcalf & Eddy 5th Ed. §6-10, the liquid-film mass-transfer coefficient
  // follows an Arrhenius-type relationship with θ = 1.024:
  //   K_L,a(T) = K_L,a(20) × 1.024^(T−20)
  // Since HTU = u_L / K_L,a:
  //   HTU(T) = HTU₂₀ / 1.024^(T−20)
  // At 85°C: HTU = HTU₂₀ / 1.024^65 = HTU₂₀ / 4.67
  const htu20    = PACKING_HTU20[p.packingType].htu;
  const thetaMT  = 1.024;   // Metcalf & Eddy θ for liquid-film controlled MT
  const HTU      = htu20 / Math.pow(thetaMT, p.temperature - 20);

  // ── 6. Tower geometry ───────────────────────────────────────────────────
  const Z = NTU * HTU;
  // VTA minimum height set by hydraulic residence time (HRT ≥ ~3 min),
  // not mass transfer. All other packing: 2.0 m structural minimum.
  const MIN_HEIGHT = p.packingType === 'vta' ? 3.0 : 2.0;
  const ZwithSF    = Z * p.safetyFactor;
  const Zdesign    = Math.max(ZwithSF, MIN_HEIGHT);
  const minHeightApplied = ZwithSF < MIN_HEIGHT;

  const A = p.flowRate / p.liquidLoading;
  const D = Math.sqrt((4 * A) / Math.PI);

  // ── 7. Steam / water vapour content ─────────────────────────────────────
  // Antoine equation for water vapour pressure (Buck, 1981):
  //   Pv = 0.61121 × exp((18.729 − T/234.9) × T/(257.87+T))  kPa
  const Pv = 0.61121 * Math.exp((18.729 - p.temperature / 234.9) * (p.temperature / (257.87 + p.temperature)));
  const waterVapourPressureKPa = Pv;
  const steamContentPct = Math.min(99, (Pv / 101.325) * 100);
  // At 85°C: Pv ≈ 57 kPa → air is ~56% water vapour at saturation.
  // Blower must handle this gas burden. Vendor to apply moisture correction
  // to actual volumetric air flow (dry-air G/L × 1/(1 − Pv/Patm)).
  const moistureWarning = p.temperature >= 70;

  // ── 8. Blower sizing — includes flare back-pressure ─────────────────────
  // ΔP_total = ΔP_packing + ΔP_inlet/outlet + ΔP_flare_header
  const Qair         = p.flowRate * p.glRatio;
  const packingDP    = 0.20;   // kPa/m (typical random packing at design loading)
  const staticHead   = Zdesign * packingDP;
  // Flare back-pressure: ammonia stripper off-gas routed to flare header;
  // typical header pressure 3–8 kPa (project basis: 5 kPa).
  const blowerPressure = staticHead + 0.5 + p.flarePressureKPa;
  const blowerPower    = (Qair * blowerPressure) / (3600 * 0.70);  // kW (η=70%)

  // ── 9. Effluent TAN (back-calculate from actual Z_design/HTU) ──────────
  const ntuActual = Zdesign / HTU;
  let tanOutActual = p.targetTAN;
  if (S > 1.001 && ntuActual > 0) {
    const x  = ((S - 1) / S) * ntuActual;
    const ex = Math.exp(x);
    tanOutActual = Math.max(0, (p.inletTAN * (1 - 1 / S)) / (ex - 1 / S));
  } else if (S <= 1.001 && ntuActual > 0) {
    tanOutActual = p.inletTAN * Math.exp(-ntuActual);
  }
  const removal = p.inletTAN > 0
    ? Math.min(100, ((p.inletTAN - tanOutActual) / p.inletTAN) * 100)
    : 0;

  // ── 10. pH dose estimate ────────────────────────────────────────────────
  // Caustic requirement dominated by alkalinity neutralisation.
  // At pH ≥ 10: representative base dose 80 g/m³ NaOH (source pH ~7.5→10.75).
  let pHDose = 0;
  if (p.reagent !== 'none') {
    pHDose = p.pH >= 10.0
      ? (p.reagent === 'NaOH' ? 80 : 120)   // g/m³ base (alkalinity added separately)
      : Math.max(40, (p.pH - 7.5) * (p.reagent === 'NaOH' ? 40 : 74) * 2);
  }

  // ── 10a. Refined caustic dose — A1 Alkalinity + A5 Free CO₂ ───────────
  // Alkalinity as CaCO₃: each mg/L CaCO₃ = 1/50 meq/L; each meq requires 40 mg/L NaOH
  //   causticDoseAlk = (Alk_mgL / 50) × 40  g/m³
  // CO₂ at operating pH: CO₂ + 2 NaOH → Na₂CO₃ + H₂O  (MW: CO₂=44, NaOH=40)
  //   causticDoseCO2 = freeCO2_mgL × (40/44)  g/m³
  // At pH ≥ 10.5, essentially all CO₂ is ionised → only matters during pH adjustment
  //   co2ionised = 1 / (1 + 10^(pKa1 − pH))  where pKa1(CO₂/HCO₃) = 6.35
  const causticDoseAlk   = p.alkalinityMgL > 0 ? (p.alkalinityMgL / 50) * 40 : 0;
  const causticDoseCO2   = p.freeCO2MgL   > 0 ? p.freeCO2MgL * (40 / 44)    : 0;
  const causticDoseTotal = pHDose + causticDoseAlk + causticDoseCO2;
  const co2ionised       = (1 / (1 + Math.pow(10, 6.35 - p.pH))) * 100;  // %

  // ── 10b. Langelier Saturation Index — scaling / CaCO₃ precipitation ───
  // LSI = pH − pHs  where pHs = (9.3 + A_tds + B_temp) − (C_ca + D_alk)
  // CaCO₃ retrograde solubility: LSI increases with temperature →
  // critical at pH 11 + 85°C where CaCO₃ will precipitate aggressively.
  // Reference: Langelier (1936); Metcalf & Eddy §13-5.
  const A_tds  = (Math.log10(Math.max(1, p.tdsFeed)) - 1) / 10;
  const B_temp = -13.12 * Math.log10(T_K) + 34.55;
  const Ca_mgL = Math.max(1, p.calciumHardnessMgL * 0.4);  // CaCO₃ → Ca²⁺ (×40/100)
  const C_ca   = Math.log10(Ca_mgL);
  const D_alk  = Math.log10(Math.max(1, p.alkalinityMgL));
  const pH_s   = (9.3 + A_tds + B_temp) - (C_ca + D_alk);
  const lsi    = p.pH - pH_s;

  const scalingDrivers: string[] = [];
  if (lsi > 0)                       scalingDrivers.push(`LSI = +${lsi.toFixed(2)} (oversaturated CaCO₃)`);
  if (p.silicaMgL > 50)              scalingDrivers.push(`Silica ${p.silicaMgL} mg/L > 50 mg/L threshold`);
  if (p.temperature >= 70)           scalingDrivers.push(`T = ${p.temperature}°C promotes retrograde CaCO₃ precipitation`);
  if (p.pH > 11)                     scalingDrivers.push(`pH > 11 — accelerates CaCO₃ / Mg(OH)₂ scale`);
  if (p.calciumHardnessMgL > 400)    scalingDrivers.push(`Ca hardness ${p.calciumHardnessMgL} mg/L (as CaCO₃) elevated`);

  const scalingRisk: StripperResult['scalingRisk'] =
    lsi >= 2.0  ? 'severe'
    : lsi >= 1.0 ? 'high'
    : lsi >= 0.5 ? 'moderate'
    : lsi >= 0   ? 'low'
    :              'none';

  // ── 10c. Material of Construction — rule engine (A4 chloride + T) ──────
  // Driving mechanism: Cl⁻ induced pitting / SCC in austenitic SS at elevated T/pH.
  // Reference: NACE MR0175 / ISO 15156; EN 10088 duplex grades.
  let mocCode = '316L SS';
  let mocRationale = 'Standard austenitic SS; suitable for ambient T, moderate Cl⁻.';
  if (p.chlorideMgL > 2000) {
    mocCode = 'Super-duplex 2507';
    mocRationale = `Cl⁻ = ${p.chlorideMgL} mg/L > 2000 mg/L → high SCC/pitting risk; super-duplex PREN ≥ 40 required.`;
  } else if (p.chlorideMgL > 500 && p.temperature > 60) {
    mocCode = 'Duplex 2205';
    mocRationale = `Cl⁻ = ${p.chlorideMgL} mg/L > 500 mg/L at T = ${p.temperature}°C > 60°C → duplex 2205 (PREN ≈ 35) to resist Cl⁻ pitting/SCC.`;
  } else if (p.temperature > 80) {
    mocCode = '316L SS / FRP (vendor confirm)';
    mocRationale = `High temperature (${p.temperature}°C) with moderate Cl⁻. 316L SS acceptable; FRP or GRP lining where cost-justified. Vendor to confirm.`;
  }

  // ── 11. DO increase (re-aeration, empirical) ────────────────────────────
  const doIncrease = Math.min(8, 0.005 * p.glRatio);

  // ── 12. Design validations ──────────────────────────────────────────────
  const validations: StripperResult['validations'] = [
    { label: 'pH ≥ 10.0 for effective stripping',             pass: p.pH >= 10.0,                       value: `pH = ${p.pH.toFixed(1)}` },
    { label: 'Operating pH 10.5–11.0 (project basis)',        pass: p.pH >= 10.5 && p.pH <= 11.0,       value: `pH = ${p.pH.toFixed(2)}` },
    { label: 'Stripping factor S ≥ 1.5',                      pass: S >= 1.5,                            value: `S = ${S.toFixed(2)}` },
    { label: 'NTU between 2 and 20 (feasible tower)',         pass: NTU >= 2 && NTU <= 20,               value: `NTU = ${NTU.toFixed(2)}` },
    { label: 'Tower design height ≤ 15 m',                    pass: Zdesign <= 15,                       value: `Z = ${Zdesign.toFixed(1)} m` },
    { label: 'Liquid loading ≤ 40 m³/m²·h (flood limit)',     pass: p.liquidLoading <= 40,               value: `QL = ${p.liquidLoading.toFixed(0)} m³/m²·h` },
    { label: 'Removal efficiency ≥ 80%',                      pass: removal >= 80,                       value: `η = ${removal.toFixed(1)}%` },
    { label: 'G/L ratio ≤ 500 (blower energy limit)',          pass: p.glRatio <= 500,                    value: `G/L = ${p.glRatio}` },
    { label: 'Feed TSS ≤ 50 mg/L (pre-treatment adequate)',   pass: p.tssFeed <= 50 || p.tssFeed === 0,  value: `TSS = ${p.tssFeed} mg/L` },
    { label: 'Feed Free Oil ≤ 10 mg/L (CPI/DNF adequate)',    pass: p.freeOilFeed <= 10 || p.freeOilFeed === 0, value: `Free Oil = ${p.freeOilFeed} mg/L` },
    { label: 'Operating temp 80–90°C (hot stripping range)',  pass: p.temperature >= 80 && p.temperature <= 90, value: `T = ${p.temperature}°C` },
  ];

  return {
    pKa, fNH3, freeNH3, nh4conc,
    ureaHydrolysisEff, ureaContrib, upsetInletTAN,
    hcc, hEff,
    waterVapourPressureKPa, steamContentPct, moistureWarning,
    S, NTU, HTU,
    Z, Zdesign, minHeightApplied, A, D,
    Qair, staticHead, blowerPressure, blowerPower,
    tanOut: tanOutActual,
    removal,
    pHDose,
    causticDoseAlk, causticDoseCO2, causticDoseTotal, co2ionised,
    lsi, scalingRisk, scalingDrivers,
    mocCode, mocRationale,
    doIncrease,
    validations,
  };
}

// ── Series Cascade Calculator ───────────────────────────────────────────────

function calcSeries(
  params: StripperParams,
  stage1Target: number,
  stage2Target: number,
  unitsPerStage: number,
): SeriesResult {
  const s1 = calcStripper({ ...params, targetTAN: stage1Target });
  const s2 = calcStripper({ ...params, inletTAN: s1.tanOut, targetTAN: stage2Target });
  const overallRemoval = params.inletTAN > 0
    ? Math.min(100, ((params.inletTAN - s2.tanOut) / params.inletTAN) * 100)
    : 0;
  return {
    stage1: s1,
    stage2: s2,
    overallRemoval,
    totalQairPerStage: s1.Qair + s2.Qair,
    totalQairAll:      (s1.Qair + s2.Qair) * unitsPerStage,
    totalBlowerPowerAll: (s1.blowerPower + s2.blowerPower) * unitsPerStage,
    finalTAN: s2.tanOut,
  };
}

// ── Format helper ────────────────────────────────────────────────────────────

const f = (v: number, d = 2) => isFinite(v) && !isNaN(v) ? v.toFixed(d) : '—';

// ── Input Slider Row ─────────────────────────────────────────────────────────

function SliderRow({
  label, value, min, max, step = 1, unit, onChange, warning,
}: {
  label: string; value: number; min: number; max: number;
  step?: number; unit: string; onChange: (v: number) => void; warning?: string;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <label className="text-xs font-medium text-slate-300">{label}</label>
        <div className="flex items-center gap-1.5">
          <input
            type="number" value={value} min={min} max={max} step={step}
            onChange={e => onChange(parseFloat(e.target.value) || 0)}
            className="w-20 text-right text-xs px-2 py-0.5 text-slate-100 focus:outline-none focus:border-sky-500 rb-inner-card-sm"
          />
          <span className="text-[11px] text-slate-500 w-14">{unit}</span>
        </div>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={e => onChange(parseFloat(e.target.value))}
        className="w-full h-1.5 accent-sky-500 cursor-pointer"
      />
      {warning && (
        <p className="text-[11px] text-amber-400 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" /> {warning}
        </p>
      )}
    </div>
  );
}

// ── Metric Card ──────────────────────────────────────────────────────────────

function MetricCard({
  label, value, unit, sub, accent = false, warn = false,
}: {
  label: string; value: string; unit?: string; sub?: string; accent?: boolean; warn?: boolean;
}) {
  return (
    <div className={`rounded-xl p-3 border ${
      warn   ? 'bg-amber-500/5 border-amber-500/30'
      : accent ? 'bg-sky-500/5 border-sky-500/20'
      :          'bg-rb-surface border-slate-800'
    }`}>
      <div className="text-[11px] text-slate-400 mb-1">{label}</div>
      <div className={`text-lg font-bold ${warn ? 'text-amber-300' : accent ? 'text-sky-300' : 'text-slate-100'}`}>
        {value}
        {unit && <span className="text-xs font-normal text-slate-400 ml-1">{unit}</span>}
      </div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

// ── Project Design Basis Panel ───────────────────────────────────────────────

function ProjectDesignBasisPanel() {
  const db = PROJECT_DESIGN_BASIS;
  const s1Removal = (((db.inletS1 - db.outletS1) / db.inletS1) * 100).toFixed(1);
  const s2Removal = (((db.outletS1 - db.outletS2) / db.outletS1) * 100).toFixed(1);
  const overallRemoval = (((db.inletS1 - db.outletS2) / db.inletS1) * 100).toFixed(1);

  return (
    <div className="space-y-5">
      {/* Process Train */}
      <div>
        <h3 className="text-[11px] font-bold text-sky-400 uppercase tracking-widest mb-3">Pre-Treatment Train</h3>
        <div className="flex flex-wrap items-center gap-1.5">
          {db.pretrain.map((stage, i) => (
            <div key={stage} className="flex items-center gap-1.5">
              <div className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${
                i === 0 ? 'bg-slate-700/50 border-slate-600 text-slate-200' :
                i === 1 ? 'bg-slate-700/50 border-slate-600 text-slate-200' :
                i === 2 ? 'bg-amber-500/10 border-amber-500/30 text-amber-300' :
                'bg-sky-500/10 border-sky-500/30 text-sky-300'
              }`}>
                {stage}
              </div>
              {i < db.pretrain.length - 1 && <ArrowRight className="w-3.5 h-3.5 text-slate-600 shrink-0" />}
            </div>
          ))}
        </div>
        <p className="text-[11px] text-slate-500 mt-2">
          Total flow: <span className="text-slate-300 font-medium">{db.totalFlow} m³/hr</span> ·
          {' '}{db.unitsPerStage} × {db.unitRatedFlow} m³/hr VTA units per stage (design at {db.flowPerUnit} m³/hr per unit)
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Design Parameters Table */}
        <div>
          <h3 className="text-[11px] font-bold text-sky-400 uppercase tracking-widest mb-2">Design Parameters</h3>
          <div className="space-y-1">
            {[
              ['Total Flowrate',   `${db.totalFlow} m³/hr`],
              ['Units — Stage 1',  `${db.unitsPerStage} × ${db.unitRatedFlow} m³/hr (VTA type, 3S)`],
              ['Units — Stage 2',  `${db.unitsPerStage} × ${db.unitRatedFlow} m³/hr (VTA type, 3S)`],
              ['Operating pH',     `${db.phRange[0]} – ${db.phRange[1]}`],
              ['Operating Temp',   `${db.tempRange[0]} – ${db.tempRange[1]} °C`],
              ['Off-gas',          'NH₃ vented to flare'],
              ['Flare back-pressure', `${db.flareBackPressure} kPa (design basis)`],
              ['Discharge standard',  db.regulation],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between items-start gap-2 text-xs px-3 py-1.5 rounded-lg bg-rb-surface border border-slate-800">
                <span className="text-slate-400 shrink-0">{k}</span>
                <span className="text-slate-200 text-right">{v}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Performance Targets */}
        <div className="space-y-4">
          <div>
            <h3 className="text-[11px] font-bold text-sky-400 uppercase tracking-widest mb-2">Ammonia Performance Targets</h3>
            <div className="rounded-xl border border-slate-700 overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-800/60">
                    <th className="text-left px-3 py-2 text-slate-400 font-medium">Location</th>
                    <th className="text-right px-3 py-2 text-slate-400 font-medium">NH₃</th>
                    <th className="text-right px-3 py-2 text-slate-400 font-medium">Removal</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-slate-800">
                    <td className="px-3 py-2 text-slate-300">Inlet to Stripper 1 (post HE)</td>
                    <td className="px-3 py-2 text-right font-mono font-bold text-rose-400">{db.inletS1.toLocaleString()} mg/L</td>
                    <td className="px-3 py-2 text-right text-slate-500">—</td>
                  </tr>
                  <tr className="border-t border-slate-800 bg-sky-500/3">
                    <td className="px-3 py-2 text-slate-300">Stripper 1 Outlet</td>
                    <td className="px-3 py-2 text-right font-mono font-bold text-amber-400">{db.outletS1.toLocaleString()} mg/L</td>
                    <td className="px-3 py-2 text-right text-emerald-400 font-medium">{s1Removal}%</td>
                  </tr>
                  <tr className="border-t border-slate-800">
                    <td className="px-3 py-2 text-slate-300">Stripper 2 Outlet (Final)</td>
                    <td className="px-3 py-2 text-right font-mono font-bold text-emerald-400">{db.outletS2} mg/L</td>
                    <td className="px-3 py-2 text-right text-emerald-400 font-medium">{s2Removal}%</td>
                  </tr>
                  <tr className="border-t border-slate-700 bg-emerald-500/5">
                    <td className="px-3 py-2 font-semibold text-slate-200">Overall Reduction</td>
                    <td className="px-3 py-2 text-right font-mono text-slate-400">{db.inletS1.toLocaleString()} → {db.outletS2}</td>
                    <td className="px-3 py-2 text-right font-bold text-emerald-400">{overallRemoval}%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Feed Quality */}
          <div>
            <h3 className="text-[11px] font-bold text-sky-400 uppercase tracking-widest mb-2">Feed Water Quality (Post CPI/DNF)</h3>
            <div className="grid grid-cols-2 gap-1">
              {([
                ['COD', `${db.cod} mg/L`],
                ['TDS', `${db.tds.toLocaleString()} mg/L`],
                ['TSS', `${db.tss} mg/L`],
                ['Free Oil', `${db.freeOil} mg/L`],
                ['BOD₅', `${db.bod} mg/L`],
                ['Urea (upset)', `${db.upsetUreaPpm.toLocaleString()} ppm`],
              ] as [string, string][]).map(([k, v]) => (
                <div key={k} className="flex justify-between text-xs px-2.5 py-1.5 rounded-lg bg-rb-surface border border-slate-800">
                  <span className="text-slate-500">{k}</span>
                  <span className="text-slate-300 font-mono">{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Upset / Integration */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="flex items-start gap-2.5 rounded-xl bg-amber-500/5 border border-amber-500/20 px-3.5 py-3">
          <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-semibold text-amber-300 mb-1">Upset Conditions — Design Margin</p>
            <p className="text-[11px] text-amber-200/80">Combined inlet NH₃ up to <strong>1 wt% (10,000 ppm)</strong> and urea up to
              {' '}<strong>1,000 ppm</strong> during process upsets. At 80–90°C, urea hydrolyses completely to NH₃
              (+567 mg/L additional load). Strippers must be validated at upset NH₃ load.</p>
          </div>
        </div>
        <div className="flex items-start gap-2.5 rounded-xl bg-sky-500/5 border border-sky-500/20 px-3.5 py-3">
          <Flame className="w-4 h-4 text-orange-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-semibold text-sky-300 mb-1">Incoming WWTP Streams</p>
            <div className="flex flex-wrap gap-1 mt-1">
              {db.incomingStreams.map(s => (
                <span key={s} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700/60 text-slate-300 border border-slate-700">{s}</span>
              ))}
            </div>
            <p className="text-[11px] text-slate-400 mt-1.5">Discharge: <span className="text-sky-300">{db.regulation}</span></p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Series Performance Panel ─────────────────────────────────────────────────

function SeriesPerformancePanel({ series, params }: { series: SeriesResult; params: StripperParams }) {
  const db = PROJECT_DESIGN_BASIS;
  const unitCount = db.unitsPerStage;

  const stageCard = (label: string, sr: StripperResult, inlet: number, target: number) => (
    <div className="rb-card p-4 space-y-3 flex-1">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-bold text-slate-200 flex items-center gap-2">
          <Layers className="w-4 h-4 text-sky-400" /> {label}
        </h4>
        <span className={`text-xs font-bold px-2 py-0.5 rounded-lg border ${
          sr.removal >= 80 ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-rose-500/10 text-rose-400 border-rose-500/20'
        }`}>{f(sr.removal, 1)}% removal</span>
      </div>

      {/* Flow path */}
      <div className="flex items-center gap-2 text-xs">
        <div className="px-2.5 py-1.5 rounded-lg bg-rose-500/10 border border-rose-500/20 text-rose-300 font-mono font-bold">
          {inlet.toLocaleString()} mg/L
        </div>
        <ArrowRight className="w-3.5 h-3.5 text-slate-600 shrink-0" />
        <div className="flex-1 text-center">
          <div className="text-[10px] text-slate-500">VTA tower</div>
          <div className="text-[10px] text-slate-500">{f(sr.Zdesign, 1)} m{sr.minHeightApplied ? ' (HRT min)' : ''}</div>
        </div>
        <ArrowRight className="w-3.5 h-3.5 text-slate-600 shrink-0" />
        <div className={`px-2.5 py-1.5 rounded-lg font-mono font-bold text-xs ${
          sr.tanOut <= target * 1.05 ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-300' : 'bg-amber-500/10 border border-amber-500/20 text-amber-300'
        }`}>
          {f(sr.tanOut, 1)} mg/L
        </div>
      </div>

      {/* Key metrics */}
      <div className="grid grid-cols-2 gap-1.5 text-[11px]">
        {[
          ['f_NH₃', `${f(sr.fNH3 * 100, 1)}%`],
          ['Hcc(T)', f(sr.hcc, 4)],
          ['S', f(sr.S, 2)],
          ['NTU', f(sr.NTU, 2)],
          ['HTU (Arrhenius)', `${f(sr.HTU, 3)} m`],
          ['Z theoretical', `${f(sr.Z, 2)} m`],
          ['Z design', `${f(sr.Zdesign, 1)} m`],
          ['Diameter / unit', `${f(sr.D, 2)} m`],
          ['Q_air / unit', `${f(sr.Qair, 0)} m³/h`],
          ['Blower ΔP', `${f(sr.blowerPressure, 2)} kPa`],
          ['Blower power / unit', `${f(sr.blowerPower, 2)} kW`],
          ['Steam content', `${f(sr.steamContentPct, 1)}%`],
        ].map(([k, v]) => (
          <div key={k} className="flex justify-between px-2 py-1 rounded bg-rb-surface border border-slate-800">
            <span className="text-slate-500">{k}</span>
            <span className="text-slate-200 font-mono">{v}</span>
          </div>
        ))}
      </div>

      {/* Validations compact */}
      <div className="space-y-1">
        {sr.validations.filter((_, i) => i < 7).map((v, i) => (
          <div key={i} className={`flex items-center gap-1.5 text-[10px] px-2 py-1 rounded border ${
            v.pass ? 'border-emerald-500/15 bg-emerald-500/3' : 'border-rose-500/20 bg-rose-500/5'
          }`}>
            {v.pass ? <CheckCircle2 className="w-3 h-3 text-emerald-400 shrink-0" /> : <AlertCircle className="w-3 h-3 text-rose-400 shrink-0" />}
            <span className={v.pass ? 'text-slate-400' : 'text-slate-300'}>{v.label}</span>
            <span className={`ml-auto font-mono ${v.pass ? 'text-emerald-400' : 'text-rose-400'}`}>{v.value}</span>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="p-5 rb-section space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-sm font-bold text-slate-200">
          <Activity className="w-4 h-4 text-sky-400" /> Two-Stage Series Performance
          <span className="text-[11px] text-slate-500 font-normal">({unitCount} × parallel units per stage)</span>
        </h2>
        <span className={`text-xs font-bold px-2.5 py-1 rounded-lg border ${
          series.overallRemoval >= 98 ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
        }`}>
          Overall: {f(series.overallRemoval, 1)}% → {f(series.finalTAN, 1)} mg/L
        </span>
      </div>

      <div className="flex flex-col lg:flex-row gap-4">
        {stageCard('Stripper 1 (per unit)', series.stage1, params.inletTAN, params.targetTAN)}
        {stageCard('Stripper 2 (per unit)', series.stage2, series.stage1.tanOut, 100)}
      </div>

      {/* Combined totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pt-2 border-t border-slate-800">
        {[
          { label: `Total Q_air (${unitCount} units × 2 stages)`, value: `${f(series.totalQairAll, 0)} m³/h`, accent: false },
          { label: `Total blower power (all units)`,              value: `${f(series.totalBlowerPowerAll, 1)} kW`,  accent: false },
          { label: 'Final effluent TAN',                          value: `${f(series.finalTAN, 1)} mg/L`,          accent: series.finalTAN <= 100 },
          { label: 'Overall NH₃ reduction',                       value: `${f(series.overallRemoval, 1)}%`,        accent: series.overallRemoval >= 98 },
        ].map(m => (
          <div key={m.label} className={`rounded-xl p-3 border text-center ${m.accent ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-rb-surface border-slate-800'}`}>
            <div className="text-[10px] text-slate-500 mb-1">{m.label}</div>
            <div className={`text-base font-bold ${m.accent ? 'text-emerald-300' : 'text-slate-100'}`}>{m.value}</div>
          </div>
        ))}
      </div>

      {/* Moisture warning */}
      {series.stage1.moistureWarning && (
        <div className="flex items-start gap-2.5 rounded-xl bg-amber-500/5 border border-amber-500/20 px-3.5 py-3">
          <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
          <p className="text-[11px] text-amber-200/90">
            <strong className="text-amber-300">Steam content warning (T = {params.temperature}°C):</strong>{' '}
            Water vapour pressure ≈ {f(series.stage1.waterVapourPressureKPa, 1)} kPa → air in tower is approximately{' '}
            <strong>{f(series.stage1.steamContentPct, 0)}% saturated</strong> with water vapour at this temperature.
            Actual blower volumetric flow must be corrected by factor ≈ 1/(1 − {f(series.stage1.waterVapourPressureKPa / 101.3, 2)}) = {f(1 / (1 - series.stage1.waterVapourPressureKPa / 101.3), 2)}.
            Blower selection, piping and knockouts must accommodate condensation; vendor to confirm.
          </p>
        </div>
      )}
    </div>
  );
}

// ── Report Modal ─────────────────────────────────────────────────────────────

function ReportModal({ params, result, onClose }: { params: StripperParams; result: StripperResult; onClose: () => void }) {
  const packLabel = PACKING_HTU20[params.packingType].label;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl bg-[#0a0f1a] border border-slate-700 shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-[#0a0f1a]">
          <div>
            <h2 className="text-base font-bold text-slate-100">Ammonia Stripping Tower — Validation Report</h2>
            <p className="text-xs text-slate-500 mt-0.5">Reynolds Bauhm Smart Water | {new Date().toLocaleDateString('en-GB')} | RCER-2025</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-800 transition-colors">
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>

        <div className="p-6 space-y-6 text-sm">
          {/* Inputs */}
          <section>
            <h3 className="font-bold text-sky-400 text-xs uppercase tracking-widest mb-3">Input Parameters</h3>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {[
                ['Flow rate (per unit)', `${params.flowRate} m³/h`],
                ['Inlet TAN',           `${params.inletTAN.toLocaleString()} mg/L`],
                ['Target effluent TAN', `${params.targetTAN} mg/L`],
                ['pH',                  params.pH.toString()],
                ['Temperature',         `${params.temperature} °C`],
                ['Packing type',        packLabel],
                ['G/L ratio',           params.glRatio.toString()],
                ['Liquid loading',      `${params.liquidLoading} m³/m²·h`],
                ['Safety factor',       params.safetyFactor.toString()],
                ['pH reagent',          REAGENT_LABELS[params.reagent]],
                ['COD feed',            `${params.codFeed} mg/L`],
                ['TDS feed',            `${params.tdsFeed.toLocaleString()} mg/L`],
                ['TSS feed',            `${params.tssFeed} mg/L`],
                ['Free oil feed',       `${params.freeOilFeed} mg/L`],
                ['BOD₅ feed',           `${params.bodFeed} mg/L`],
                ['Urea (upset)',         `${params.ureaPpm} mg/L`],
                ['Flare back-pressure', `${params.flarePressureKPa} kPa`],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between bg-rb-surface rounded px-3 py-1.5 border border-slate-800">
                  <span className="text-slate-400">{k}</span>
                  <span className="text-slate-100 font-medium">{v}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Calculation breakdown */}
          <section>
            <h3 className="font-bold text-sky-400 text-xs uppercase tracking-widest mb-3">Calculation Breakdown</h3>
            <div className="space-y-2 text-xs font-mono">
              {[
                ['Urea hydrolysis eff.',    `${f(result.ureaHydrolysisEff * 100, 0)}% at ${params.temperature}°C → +${f(result.ureaContrib, 1)} mg/L NH₃ (upset inlet = ${f(result.upsetInletTAN, 0)} mg/L)`],
                ['pKa(T)',                   `0.09018 + 2729.92 / ${f(params.temperature + 273.15, 2)} = ${f(result.pKa, 4)}`],
                ['f_NH₃',                   `1 / (1 + 10^(${f(result.pKa, 4)} − ${params.pH})) = ${f(result.fNH3, 4)} (${f(result.fNH3 * 100, 1)}%)`],
                ['Free NH₃',                `${params.inletTAN} × ${f(result.fNH3, 4)} = ${f(result.freeNH3, 2)} mg/L`],
                ['Hcc(T)',                   `0.01144 × exp(4092 × (1/293.15 − 1/${f(params.temperature + 273.15, 2)})) = ${f(result.hcc, 5)}`],
                ['H_eff',                   `${f(result.hcc, 5)} × ${f(result.fNH3, 4)} = ${f(result.hEff, 5)}`],
                ['S = H_eff × G/L',         `${f(result.hEff, 5)} × ${params.glRatio} = ${f(result.S, 3)}`],
                ['NTU',                     `(${f(result.S, 3)}/(${f(result.S, 3)}−1)) × ln[…] = ${f(result.NTU, 3)}`],
                ['HTU(T) — Arrhenius',      `${PACKING_HTU20[params.packingType].htu} / 1.024^(${params.temperature}−20) = ${f(result.HTU, 4)} m`],
                ['Z = NTU × HTU',           `${f(result.NTU, 3)} × ${f(result.HTU, 4)} = ${f(result.Z, 3)} m`],
                ['Z_design',                `max(${f(result.Z, 3)} × ${params.safetyFactor}, min_height) = ${f(result.Zdesign, 2)} m${result.minHeightApplied ? ' ← HRT minimum applied' : ''}`],
                ['A = Q/QL',                `${params.flowRate} / ${params.liquidLoading} = ${f(result.A, 2)} m²`],
                ['D = √(4A/π)',             `√(4 × ${f(result.A, 2)} / π) = ${f(result.D, 2)} m`],
                ['Q_air',                   `${params.flowRate} × ${params.glRatio} = ${f(result.Qair, 0)} m³/h`],
                ['Water vapour pressure',   `${f(result.waterVapourPressureKPa, 2)} kPa (${f(result.steamContentPct, 1)}% steam content)`],
                ['ΔP_blower',               `${f(result.staticHead, 2)} packing + 0.5 losses + ${params.flarePressureKPa} flare = ${f(result.blowerPressure, 2)} kPa`],
                ['Blower power',            `${f(result.Qair, 0)} × ${f(result.blowerPressure, 2)} / (3600 × 0.7) = ${f(result.blowerPower, 2)} kW`],
                ['Effluent TAN',            `${f(result.tanOut, 2)} mg/L (η = ${f(result.removal, 1)}%)`],
              ].map(([k, v]) => (
                <div key={k} className="grid grid-cols-5 gap-2 bg-rb-surface rounded px-3 py-1.5 border border-slate-800">
                  <span className="text-slate-400 col-span-2">{k}</span>
                  <span className="text-sky-300 col-span-3">{v}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Validation */}
          <section>
            <h3 className="font-bold text-sky-400 text-xs uppercase tracking-widest mb-3">Validation Summary</h3>
            <div className="space-y-1.5">
              {result.validations.map((v, i) => (
                <div key={i} className={`flex items-center justify-between rounded-lg px-3 py-2 text-xs border ${
                  v.pass ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-rose-500/5 border-rose-500/20'
                }`}>
                  <div className="flex items-center gap-2">
                    {v.pass ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <AlertCircle className="w-3.5 h-3.5 text-rose-400" />}
                    <span className={v.pass ? 'text-slate-200' : 'text-slate-300'}>{v.label}</span>
                  </div>
                  <span className={`font-mono font-bold ${v.pass ? 'text-emerald-400' : 'text-rose-400'}`}>{v.value}</span>
                </div>
              ))}
            </div>
          </section>

          {/* References */}
          <section>
            <h3 className="font-bold text-sky-400 text-xs uppercase tracking-widest mb-2">References & Standards</h3>
            <ul className="text-xs text-slate-500 space-y-1 list-disc list-inside">
              <li>Metcalf & Eddy, <em>Wastewater Engineering: Treatment and Resource Recovery</em>, 5th Ed., McGraw-Hill (2014)</li>
              <li>US EPA Design Manual — Ammonia Stripping (EPA/625/1-74/016)</li>
              <li>Staudinger & Roberts, "Henry's law constant temperature dependence", <em>Chemosphere</em> 44 (2001)</li>
              <li>WEF MOP 8 — <em>Design of Municipal Wastewater Treatment Plants</em></li>
              <li>Strigle, <em>Packed Tower Design and Applications</em>, 2nd Ed., Gulf Publishing (1994)</li>
              <li>Bates & Pinching, pKa temperature dependence, <em>J. Res. NBS</em> 42 (1949)</li>
              <li>Royal Commission Environmental Regulations 2025 (RCER-2025)</li>
            </ul>
          </section>
        </div>

        <div className="px-6 pb-6 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-rb-brand text-white text-sm font-semibold hover:bg-rb-brand/90 transition-colors">
            Close Report
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Clarification Register Panel (A–D structured table) ─────────────────────

function ClarificationRegisterPanel({ params, result }: { params: StripperParams; result: StripperResult }) {
  const rows: { ref: string; item: string; status: 'open' | 'noted' | 'closed'; value: string; action: string }[] = [
    {
      ref: 'A1',
      item: 'Caustic soda — total NaOH dose at design pH',
      status: 'closed',
      value: `${f(result.causticDoseTotal, 0)} g/m³ (pH base ${f(result.pHDose, 0)} + alk ${f(result.causticDoseAlk, 0)} + CO₂ ${f(result.causticDoseCO2, 0)})`,
      action: 'Include in chemical dosing design; size caustic storage and dilution system accordingly.',
    },
    {
      ref: 'A2',
      item: 'Scaling risk — Langelier Saturation Index at operating T/pH',
      status: result.scalingRisk === 'none' || result.scalingRisk === 'low' ? 'closed' : result.scalingRisk === 'moderate' ? 'noted' : 'open',
      value: `LSI = ${f(result.lsi, 2)} (${result.scalingRisk.toUpperCase()}) · Ca hardness ${params.calciumHardnessMgL} mg/L (CaCO₃)`,
      action: result.scalingRisk === 'severe' || result.scalingRisk === 'high'
        ? 'REQUIRED: Antiscalant dosing and/or acid softening upstream. Inspect tower internals monthly.'
        : result.scalingRisk === 'moderate'
        ? 'Monitor scale deposition. Consider threshold antiscalant dosing. 3-monthly internal inspection.'
        : 'Routine monitoring. No antiscalant required at this LSI.',
    },
    {
      ref: 'A3',
      item: 'Silica — amorphous silica scale potential',
      status: params.silicaMgL > 100 ? 'open' : params.silicaMgL > 50 ? 'noted' : 'closed',
      value: `SiO₂ = ${params.silicaMgL} mg/L at pH ${params.pH}`,
      action: params.silicaMgL > 100
        ? 'REQUIRED: Silica control — reduce TDS concentration ratio or dose silica dispersant. Confirm with vendor.'
        : params.silicaMgL > 50
        ? 'Silica monitoring required. Consider dispersant dosing above 50 mg/L at pH > 10.'
        : 'Within acceptable range. No action required.',
    },
    {
      ref: 'A4',
      item: 'Material of Construction — Cl⁻ pitting / SCC resistance',
      status: 'closed',
      value: `${result.mocCode} — Cl⁻ = ${params.chlorideMgL} mg/L, T = ${params.temperature}°C`,
      action: result.mocRationale,
    },
    {
      ref: 'A5',
      item: 'Free CO₂ — ionisation at operating pH',
      status: 'closed',
      value: `${params.freeCO2MgL} mg/L free CO₂ → ${f(result.co2ionised, 2)}% ionised at pH ${params.pH}`,
      action: 'Free CO₂ effectively zero at pH ≥ 10.5. CO₂ load applies to caustic during pH-up step only.',
    },
    {
      ref: 'A6',
      item: 'Urea hydrolysis — NH₃ upset load from urea',
      status: params.ureaPpm > 0 ? 'closed' : 'noted',
      value: params.ureaPpm > 0
        ? `${params.ureaPpm} ppm urea → +${f(result.ureaContrib, 1)} mg/L NH₃ at ${params.temperature}°C (eff ${f(result.ureaHydrolysisEff * 100, 0)}%)`
        : 'No urea specified',
      action: params.ureaPpm > 0
        ? `Validate strippers at upset inlet TAN = ${f(result.upsetInletTAN, 0)} mg/L. Include in blower and flare sizing.`
        : 'Confirm urea concentration with process team if applicable.',
    },
    {
      ref: 'A8',
      item: 'Direct NH₃ upset — peak inlet concentration',
      status: params.upsetNH3Direct > 0 ? (params.upsetNH3Direct > 5000 ? 'open' : 'noted') : 'noted',
      value: params.upsetNH3Direct > 0
        ? `${params.upsetNH3Direct.toLocaleString()} ppm peak NH₃ at inlet`
        : 'Not specified — use project basis (10,000 ppm per RCER-2025 design basis)',
      action: params.upsetNH3Direct > 5000
        ? 'CRITICAL: Validate blower, tower and flare system at full upset load. High-high NH₃ trip interlock required.'
        : 'Include in upset scenario validation. Confirm with process team.',
    },
    {
      ref: 'B',
      item: 'Utilities — steam (heat exchanger), instrument air, electrical',
      status: 'open',
      value: 'Feed heated to 80–90°C via heat exchanger upstream',
      action: 'Confirm steam supply pressure and condensate return. Verify blower motor sizing (incl. VFD if applicable). Instrument air dew point ≤ −40°C.',
    },
    {
      ref: 'C',
      item: 'Off-gas interface — ammonia flare header',
      status: 'noted',
      value: `Flare back-pressure = ${params.flarePressureKPa} kPa (project basis: 3–8 kPa)`,
      action: 'Confirm flare header pressure at maximum load. Blower discharge to include non-return valve. NH₃ analyser on off-gas line.',
    },
    {
      ref: 'D',
      item: 'Design codes — pressure vessels, piping, structural',
      status: 'noted',
      value: 'AS 1210 (PV) · AS 4041 (piping) · AS/NZS 3000 (elect) · RCER-2025 (discharge)',
      action: 'Vendor to confirm structural and pressure envelope design to applicable Australian codes. HAZOP required prior to detailed engineering.',
    },
  ];

  const statusColour = {
    open:   'bg-rose-500/10 border-rose-500/20 text-rose-400',
    noted:  'bg-amber-500/10 border-amber-500/20 text-amber-400',
    closed: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
  };

  return (
    <div className="p-5 rb-section space-y-4">
      <div className="flex items-center gap-2">
        <ClipboardList className="w-4 h-4 text-sky-400" />
        <h2 className="text-sm font-bold text-slate-200">Clarification Register — Industry Standard Items (A–D)</h2>
      </div>
      <p className="text-[11px] text-slate-500">
        Structured record of key technical clarifications per industry standard checklist. Items are computed from current input parameters.
        Status: <span className="text-rose-400">OPEN</span> = action required ·
        {' '}<span className="text-amber-400">NOTED</span> = monitor/confirm ·
        <span className="text-emerald-400"> CLOSED</span> = resolved by calculation.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="border-b border-slate-700">
              {['Ref', 'Item', 'Status', 'Calculated Value', 'Required Action'].map(h => (
                <th key={h} className="text-left py-2 px-3 text-slate-400 font-medium whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.ref} className="border-b border-slate-800/60 hover:bg-slate-800/20">
                <td className="py-2 px-3 font-bold text-sky-400 whitespace-nowrap">{r.ref}</td>
                <td className="py-2 px-3 text-slate-300 font-medium max-w-[180px]">{r.item}</td>
                <td className="py-2 px-3">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${statusColour[r.status]}`}>
                    {r.status.toUpperCase()}
                  </span>
                </td>
                <td className="py-2 px-3 text-slate-300 font-mono text-[11px] max-w-[200px]">{r.value}</td>
                <td className="py-2 px-3 text-slate-400 text-[10px] max-w-[200px]">{r.action}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Performance Guarantee Panel ───────────────────────────────────────────────

function PerformanceGuaranteePanel({ params, result, series }: { params: StripperParams; result: StripperResult; series: SeriesResult }) {
  const db = PROJECT_DESIGN_BASIS;
  const conditions = [
    {
      no: '1',
      condition: 'Inlet flow rate',
      specified: `${params.flowRate} m³/h per unit (${db.totalFlow} m³/h total, ${db.unitsPerStage} units)`,
      basis: 'Process design basis RCER-2025',
    },
    {
      no: '2',
      condition: 'Inlet TAN concentration',
      specified: `${params.inletTAN.toLocaleString()} mg/L TAN (design) · ${f(result.upsetInletTAN, 0)} mg/L (upset incl. urea)`,
      basis: 'After heat exchanger, pre Stripper 1',
    },
    {
      no: '3',
      condition: 'Operating temperature',
      specified: `${params.temperature}°C (range ${db.tempRange[0]}–${db.tempRange[1]}°C)`,
      basis: 'Heat exchanger outlet; VTA vendor to confirm HTU₂₀ and pilot data',
    },
    {
      no: '4',
      condition: 'Operating pH',
      specified: `pH ${params.pH.toFixed(2)} (range ${db.phRange[0]}–${db.phRange[1]})`,
      basis: 'NaOH dosing upstream of stripper',
    },
  ];

  return (
    <div className="p-5 rb-section space-y-4">
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-emerald-400" />
        <h2 className="text-sm font-bold text-slate-200">Performance Guarantee — Formal Notice</h2>
      </div>

      {/* Guarantee statement */}
      <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 px-4 py-3 space-y-2">
        <p className="text-xs font-semibold text-emerald-300">
          Guarantee: Ammonia Stripping — Two-Stage VTA Series (RCER-2025)
        </p>
        <p className="text-[11px] text-emerald-200/80">
          The ammonia stripping system, comprising two VTA towers in series ({db.unitsPerStage} parallel units per stage),
          is designed to achieve a final effluent TAN concentration of{' '}
          <strong>≤ {db.overallTarget} mg/L</strong> from an inlet TAN of{' '}
          <strong>{params.inletTAN.toLocaleString()} mg/L</strong> under the following guaranteed operating conditions.
          This guarantee is contingent on all four conditions below being met simultaneously.
          Performance outside these envelopes voids the guarantee and requires a design review.
        </p>
      </div>

      {/* Guarantee conditions */}
      <div className="rounded-xl border border-slate-700 overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-800/60 border-b border-slate-700">
              <th className="text-left px-3 py-2 text-slate-400 font-medium w-8">No.</th>
              <th className="text-left px-3 py-2 text-slate-400 font-medium">Condition</th>
              <th className="text-left px-3 py-2 text-slate-400 font-medium">Specified Value</th>
              <th className="text-left px-3 py-2 text-slate-400 font-medium">Basis</th>
            </tr>
          </thead>
          <tbody>
            {conditions.map(c => (
              <tr key={c.no} className="border-t border-slate-800">
                <td className="px-3 py-2 font-bold text-sky-400">{c.no}</td>
                <td className="px-3 py-2 text-slate-300 font-medium">{c.condition}</td>
                <td className="px-3 py-2 text-slate-200 font-mono">{c.specified}</td>
                <td className="px-3 py-2 text-slate-500">{c.basis}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Calculated guarantee outcome */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className={`rounded-xl p-3 border text-center ${series.finalTAN <= db.overallTarget ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-rose-500/5 border-rose-500/20'}`}>
          <div className="text-[10px] text-slate-500 mb-1">Guaranteed effluent TAN</div>
          <div className={`text-lg font-bold ${series.finalTAN <= db.overallTarget ? 'text-emerald-300' : 'text-rose-400'}`}>≤ {db.overallTarget} mg/L</div>
          <div className="text-[10px] text-slate-500 mt-0.5">RCER-2025 limit</div>
        </div>
        <div className={`rounded-xl p-3 border text-center ${series.finalTAN <= db.overallTarget ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-rose-500/5 border-rose-500/20'}`}>
          <div className="text-[10px] text-slate-500 mb-1">Predicted effluent TAN</div>
          <div className={`text-lg font-bold ${series.finalTAN <= db.overallTarget ? 'text-emerald-300' : 'text-rose-400'}`}>{f(series.finalTAN, 1)} mg/L</div>
          <div className="text-[10px] text-slate-500 mt-0.5">{series.finalTAN <= db.overallTarget ? '✓ Meets guarantee' : '✗ Below target — adjust parameters'}</div>
        </div>
        <div className={`rounded-xl p-3 border text-center ${series.overallRemoval >= 98 ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-amber-500/5 border-amber-500/20'}`}>
          <div className="text-[10px] text-slate-500 mb-1">Overall NH₃ removal</div>
          <div className={`text-lg font-bold ${series.overallRemoval >= 98 ? 'text-emerald-300' : 'text-amber-400'}`}>{f(series.overallRemoval, 1)}%</div>
          <div className="text-[10px] text-slate-500 mt-0.5">Target ≥ 98.5%</div>
        </div>
      </div>

      {/* Scaling / MoC risk flags */}
      {(result.scalingRisk === 'high' || result.scalingRisk === 'severe' || result.mocCode.includes('Duplex') || result.mocCode.includes('FRP')) && (
        <div className="space-y-2">
          {(result.scalingRisk === 'high' || result.scalingRisk === 'severe') && (
            <div className="flex items-start gap-2.5 rounded-xl bg-rose-500/5 border border-rose-500/20 px-3.5 py-3">
              <AlertTriangle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-rose-300 mb-1">Scaling Risk — {result.scalingRisk.toUpperCase()} (LSI = {f(result.lsi, 2)})</p>
                <ul className="text-[11px] text-rose-200/80 space-y-0.5 list-disc list-inside">
                  {result.scalingDrivers.map((d, i) => <li key={i}>{d}</li>)}
                </ul>
                <p className="text-[11px] text-rose-200/70 mt-1">Antiscalant dosing and/or upstream softening required. Guarantee performance subject to chemical pre-treatment compliance.</p>
              </div>
            </div>
          )}
          {(result.mocCode.includes('Duplex') || result.mocCode.includes('Super')) && (
            <div className="flex items-start gap-2.5 rounded-xl bg-amber-500/5 border border-amber-500/20 px-3.5 py-3">
              <ShieldCheck className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-amber-300 mb-1">MoC Upgrade Required — {result.mocCode}</p>
                <p className="text-[11px] text-amber-200/80">{result.mocRationale}</p>
              </div>
            </div>
          )}
        </div>
      )}

      <p className="text-[10px] text-slate-500 italic">
        This performance guarantee is preliminary and based on the NTU/HTU design method with Arrhenius temperature correction (Metcalf & Eddy 5th Ed.).
        VTA vendor must confirm HTU₂₀ via pilot testing or proprietary performance data before guarantee is binding.
        Design codes: AS 1210 · AS 4041 · AS/NZS 3000 · RCER-2025.
      </p>
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────

export default function AmmoniaStripperSpecPage() {
  const [params, setParams]         = useState<StripperParams>(DEFAULT_PARAMS);
  const [showReport, setShowReport] = useState(false);
  const [sciOpen, setSciOpen]       = useState(false);
  const [dbOpen, setDbOpen]         = useState(false);
  const [showFeed, setShowFeed]     = useState(false);

  const set = useCallback(<K extends keyof StripperParams>(key: K, value: StripperParams[K]) => {
    setParams(prev => ({ ...prev, [key]: value }));
  }, []);

  const result = useMemo(() => calcStripper(params), [params]);

  const seriesResult = useMemo(() =>
    calcSeries(params, params.targetTAN, 100, PROJECT_DESIGN_BASIS.unitsPerStage),
    [params]);

  const handleReset = () => { setParams(DEFAULT_PARAMS); toast.success('Parameters reset to defaults'); };
  const handleLoadProject = () => { setParams(VTA_PROJECT_PARAMS); setDbOpen(true); toast.success('Project design basis loaded — 3S VTA configuration'); };

  // ── Chart data ───────────────────────────────────────────────────────────
  const phChartData = useMemo(() =>
    Array.from({ length: 25 }, (_, i) => {
      const pH = 6 + i * 0.25;
      const r = calcStripper({ ...params, pH });
      return { pH: parseFloat(pH.toFixed(2)), removal: parseFloat(r.removal.toFixed(1)), S: parseFloat(r.S.toFixed(2)) };
    }), [params]);

  const tempChartData = useMemo(() =>
    Array.from({ length: 18 }, (_, i) => {
      const temp = 5 + i * 5;
      const r = calcStripper({ ...params, temperature: temp });
      return { temp, Zdesign: parseFloat(r.Zdesign.toFixed(2)), removal: parseFloat(r.removal.toFixed(1)), S: parseFloat(r.S.toFixed(2)) };
    }), [params]);

  // Must be declared before scienceSections useMemo (TDZ guard)
  const htu20 = PACKING_HTU20[params.packingType].htu;

  // ── Science accordion sections ───────────────────────────────────────────
  const scienceSections = useMemo((): ScienceSection[] => {
    const T_K = params.temperature + 273.15;
    const r = result;
    return [
      {
        title: 'Step 0 — Urea Hydrolysis at High Temperature',
        icon: FlaskConical,
        equations: [
          {
            label: 'Urea hydrolysis reaction',
            formula: 'CO(NH₂)₂ + H₂O → CO₂ + 2 NH₃   (catalysed by urease; also thermal at T > 60°C)',
            substitution: `MW: urea = 60 g/mol, NH₃ = 17 g/mol → 1 mg/L urea produces ${f(2*17/60, 3)} mg/L NH₃`,
            result: `${f(2*17/60, 3)} mg/L NH₃ per mg/L urea`,
            status: 'pass',
            unit: 'stoichiometric yield',
          },
          {
            label: 'Thermal hydrolysis efficiency at operating temperature',
            formula: 'eff = 0 (T<40°C), (T−40)/40 (40–80°C), 1.0 (T≥80°C)',
            substitution: `At ${params.temperature}°C: eff = ${f(r.ureaHydrolysisEff * 100, 0)}%`,
            result: `${f(r.ureaHydrolysisEff * 100, 0)}%`,
            status: r.ureaHydrolysisEff > 0.9 ? 'pass' : r.ureaHydrolysisEff > 0.5 ? 'warn' : 'fail',
          },
          {
            label: 'NH₃ contribution from urea (upset scenario)',
            formula: 'NH₃_urea = Urea_ppm × (2×17/60) × eff',
            substitution: `${params.ureaPpm} × 0.5667 × ${f(r.ureaHydrolysisEff, 3)} = ${f(r.ureaContrib, 1)} mg/L`,
            result: `+${f(r.ureaContrib, 1)} mg/L`,
            status: 'pass',
            unit: 'additional NH₃ load',
          },
          {
            label: 'Upset-condition inlet TAN',
            formula: 'TAN_upset = TAN_design + NH₃_urea',
            substitution: `${params.inletTAN.toLocaleString()} + ${f(r.ureaContrib, 1)} = ${f(r.upsetInletTAN, 0)} mg/L`,
            result: `${f(r.upsetInletTAN, 0)} mg/L`,
            status: 'pass',
            unit: '— validate strippers at this load',
          },
        ],
        warnings: params.ureaPpm > 0 && r.ureaHydrolysisEff > 0.5 ? [`At ${params.temperature}°C, ${f(r.ureaHydrolysisEff*100,0)}% of urea converts to NH₃ — increases inlet TAN by ${f(r.ureaContrib,1)} mg/L during upset. Strippers MUST be validated at ${f(r.upsetInletTAN,0)} mg/L.`] : undefined,
      },
      {
        title: 'Step 1 — NH₃/NH₄⁺ Equilibrium (Temperature-Corrected)',
        icon: Beaker,
        equations: [
          {
            label: 'pKa at temperature T (Bates & Pinching, 1949)',
            formula: 'pKa(T) = 0.09018 + 2729.92 / T_K',
            substitution: `pKa(${params.temperature}°C) = 0.09018 + 2729.92 / ${f(T_K, 2)} = ${f(r.pKa, 4)}`,
            result: f(r.pKa, 4),
            status: 'pass',
          },
          {
            label: 'Free ammonia fraction f_NH₃ (Henderson–Hasselbalch)',
            formula: 'f_NH₃ = 1 / (1 + 10^(pKa − pH))',
            substitution: `f_NH₃ = 1 / (1 + 10^(${f(r.pKa, 4)} − ${params.pH})) = ${f(r.fNH3, 4)} (${f(r.fNH3*100,1)}%)`,
            result: `${f(r.fNH3*100,1)}%`,
            status: r.fNH3 > 0.8 ? 'pass' : r.fNH3 > 0.5 ? 'warn' : 'fail',
            unit: 'fraction of TAN as volatile NH₃',
          },
          {
            label: 'Free NH₃ concentration (volatile, strippable)',
            formula: '[NH₃] = TAN × f_NH₃',
            substitution: `${params.inletTAN} × ${f(r.fNH3,4)} = ${f(r.freeNH3,2)} mg/L`,
            result: `${f(r.freeNH3,2)} mg/L`,
            status: 'pass',
          },
          {
            label: 'NH₄⁺ concentration (ionic, non-volatile)',
            formula: '[NH₄⁺] = TAN × (1 − f_NH₃)',
            substitution: `${params.inletTAN} × ${f(1-r.fNH3,4)} = ${f(r.nh4conc,2)} mg/L`,
            result: `${f(r.nh4conc,2)} mg/L`,
            status: 'pass',
            unit: '(non-volatile — remains in effluent)',
          },
        ],
        warnings: params.pH < 10 ? [`pH ${params.pH} gives only ${f(r.fNH3*100,1)}% free NH₃. Raise to ≥10.0 for effective stripping.`] : undefined,
      },
      {
        title: "Step 2 — Henry's Law & TDS Salting-Out Effect",
        icon: Beaker,
        equations: [
          {
            label: "Dimensionless Henry's constant Hcc (Staudinger & Roberts, 2001)",
            formula: 'Hcc(T) = 0.01144 × exp[4092 × (1/293.15 − 1/T_K)]',
            substitution: `Hcc(${params.temperature}°C) = 0.01144 × exp(${f(4092*(1/293.15-1/T_K),4)}) = ${f(r.hcc,5)}`,
            result: f(r.hcc,5),
            status: 'pass',
            unit: 'mol_gas / mol_liq (±15% at T > 70°C)',
          },
          {
            label: 'Setchenow salting-out correction for TDS',
            formula: 'H_salt = H_pure × 10^(Ks × Cs)   [Ks ≈ 0.1 L/mol, Cs = ionic strength]',
            substitution: `TDS ${params.tdsFeed.toLocaleString()} mg/L → Cs ≈ ${f(params.tdsFeed/58440,4)} mol/L → correction = ${f(Math.pow(10,0.1*params.tdsFeed/58440),4)}`,
            result: `${f(Math.pow(10,0.1*params.tdsFeed/58440)*100-100,2)}% Hcc increase`,
            status: 'pass',
            unit: 'small correction at TDS 5000 mg/L',
          },
          {
            label: 'Effective Henry constant (only free NH₃ is volatile)',
            formula: 'H_eff = Hcc × f_NH₃',
            substitution: `H_eff = ${f(r.hcc,5)} × ${f(r.fNH3,4)} = ${f(r.hEff,5)}`,
            result: f(r.hEff,5),
            status: r.hEff > 0.01 ? 'pass' : r.hEff > 0.001 ? 'warn' : 'fail',
          },
        ],
      },
      {
        title: 'Step 3 — Stripping Factor & NTU',
        icon: TrendingUp,
        equations: [
          {
            label: 'Stripping factor S = H_eff × (G/L)',
            formula: 'S > 1: net transfer from liquid to gas phase; S >> 1: highly efficient',
            substitution: `S = ${f(r.hEff,5)} × ${params.glRatio} = ${f(r.S,3)}`,
            result: f(r.S,3),
            status: r.S >= 5 ? 'pass' : r.S >= 1.5 ? 'warn' : 'fail',
            unit: r.S >= 10 ? '(excellent — hot stripping regime)' : r.S >= 5 ? '(good)' : r.S >= 1.5 ? '(marginal)' : '(POOR)',
          },
          {
            label: 'NTU for countercurrent tower (Onda et al., 1968)',
            formula: 'NTU = [S/(S−1)] × ln[(Cin/Cout)×(1−1/S) + 1/S]',
            substitution: `NTU = [${f(r.S,3)}/${f(r.S-1,3)}] × ln[…] = ${f(r.NTU,3)}`,
            result: f(r.NTU,3),
            status: r.NTU >= 2 && r.NTU <= 20 ? 'pass' : 'warn',
            unit: 'transfer units required',
          },
        ],
        warnings: r.S < 1.5 ? [`S = ${f(r.S,2)} < 1.5. Increase G/L ratio or raise pH.`] : undefined,
      },
      {
        title: 'Step 4 — Arrhenius HTU Correction (Critical for Hot Stripping)',
        icon: Thermometer,
        equations: [
          {
            label: 'HTU temperature correction — Arrhenius model (Metcalf & Eddy §6-10)',
            formula: 'HTU(T) = HTU₂₀ / θ^(T−20)   where θ = 1.024 for liquid-film MT',
            substitution: `HTU(${params.temperature}°C) = ${htu20} / 1.024^${params.temperature-20} = ${htu20} / ${f(Math.pow(1.024,params.temperature-20),3)} = ${f(r.HTU,4)} m`,
            result: `${f(r.HTU,4)} m`,
            status: 'pass',
            unit: `HTU₂₀ = ${htu20} m (${PACKING_HTU20[params.packingType].label})`,
          },
          {
            label: 'Previous linear model comparison (INVALID above ~50°C)',
            formula: 'HTU_linear = HTU₂₀ × (1 − 0.015 × (T−20))  [breaks down above ~50°C, negative at >87°C]',
            substitution: `HTU_linear(${params.temperature}°C) = ${htu20} × ${f(1-0.015*(params.temperature-20),3)} = ${f(htu20*(1-0.015*(params.temperature-20)),3)} m`,
            result: `${f(htu20*(1-0.015*(params.temperature-20)),3)} m ${params.temperature > 70 ? '⚠ INVALID' : '(reasonable)'}`,
            status: params.temperature > 50 ? 'fail' : 'pass',
            unit: 'Arrhenius model used instead',
          },
          {
            label: 'Tower height Z = NTU × HTU',
            formula: 'Z (theoretical) = NTU × HTU(T)',
            substitution: `Z = ${f(r.NTU,3)} × ${f(r.HTU,4)} = ${f(r.Z,3)} m`,
            result: `${f(r.Z,3)} m`,
            status: 'pass',
          },
          {
            label: 'Design height — includes safety factor and VTA minimum HRT constraint',
            formula: 'Z_design = max(Z × SF, Z_min)   [VTA: Z_min = 3.0 m for HRT ≥ 3 min]',
            substitution: `Z_design = max(${f(r.Z,3)} × ${params.safetyFactor}, ${params.packingType==='vta'?'3.0':'2.0'}) = ${f(r.Zdesign,2)} m${r.minHeightApplied?' ← HRT minimum applied':''}`,
            result: `${f(r.Zdesign,2)} m`,
            status: r.Zdesign <= 15 ? 'pass' : r.Zdesign <= 20 ? 'warn' : 'fail',
          },
        ],
        warnings: r.minHeightApplied ? [`At ${params.temperature}°C, mass-transfer limited height is only ${f(r.Z,2)} m — tower is sized to ${f(r.Zdesign,1)} m by VTA hydraulic residence time (HRT) minimum. Vendor to confirm HRT-based sizing.`] : undefined,
      },
      {
        title: 'Step 5 — Steam Content & Blower Sizing (inc. Flare Back-Pressure)',
        icon: Wind,
        equations: [
          {
            label: 'Water vapour pressure at operating temperature (Buck equation)',
            formula: 'Pv = 0.61121 × exp[(18.729 − T/234.9) × T/(257.87+T)]  kPa',
            substitution: `Pv(${params.temperature}°C) = ${f(r.waterVapourPressureKPa,2)} kPa`,
            result: `${f(r.waterVapourPressureKPa,2)} kPa`,
            status: r.waterVapourPressureKPa < 50 ? 'pass' : 'warn',
            unit: `${f(r.steamContentPct,1)}% steam content at saturation`,
          },
          {
            label: 'Air flow rate Q_air (dry-air basis per unit)',
            formula: 'Q_air = Q_water × G/L',
            substitution: `Q_air = ${params.flowRate} × ${params.glRatio} = ${f(r.Qair,0)} m³/h`,
            result: `${f(r.Qair,0)} m³/h`,
            status: 'pass',
          },
          {
            label: 'Wet-gas correction at high temperature',
            formula: 'Q_wet = Q_dry / (1 − Pv/Patm)   [blower must handle water-vapour-laden gas]',
            substitution: `Q_wet = ${f(r.Qair,0)} / (1 − ${f(r.waterVapourPressureKPa/101.325,3)}) = ${f(r.Qair/(1-r.waterVapourPressureKPa/101.325),0)} m³/h`,
            result: `${f(r.Qair/(1-r.waterVapourPressureKPa/101.325),0)} m³/h`,
            status: r.steamContentPct > 40 ? 'warn' : 'pass',
            unit: 'wet volumetric flow (vendor to confirm)',
          },
          {
            label: 'Packing pressure drop',
            formula: 'ΔP_packing = 0.20 kPa/m × Z_design',
            substitution: `ΔP = 0.20 × ${f(r.Zdesign,2)} = ${f(r.staticHead,2)} kPa`,
            result: `${f(r.staticHead,2)} kPa`,
            status: 'pass',
          },
          {
            label: 'Total blower pressure — includes ammonia flare header back-pressure',
            formula: 'ΔP_total = ΔP_packing + 0.5 (losses) + ΔP_flare',
            substitution: `ΔP_total = ${f(r.staticHead,2)} + 0.5 + ${params.flarePressureKPa} = ${f(r.blowerPressure,2)} kPa`,
            result: `${f(r.blowerPressure,2)} kPa`,
            status: 'pass',
            unit: `flare back-pressure = ${params.flarePressureKPa} kPa`,
          },
          {
            label: 'Blower shaft power (η = 70%)',
            formula: 'P = Q_air × ΔP / (3600 × 0.70)  kW',
            substitution: `P = ${f(r.Qair,0)} × ${f(r.blowerPressure,2)} / 2520 = ${f(r.blowerPower,2)} kW`,
            result: `${f(r.blowerPower,2)} kW`,
            status: 'pass',
          },
        ],
        warnings: r.moistureWarning ? [`T = ${params.temperature}°C — water vapour pressure = ${f(r.waterVapourPressureKPa,1)} kPa (${f(r.steamContentPct,0)}% steam content). Blower must be sized for wet-gas volumetric flow. Moisture knockout/separator required on blower discharge.`] : undefined,
      },
      {
        title: 'Step 6 — Effluent Quality & VTA Technology Notes',
        icon: Target,
        equations: [
          {
            label: 'Estimated effluent TAN (back-calculated from Z_design / HTU)',
            formula: 'C_out = C_in × (1 − 1/S) / (e^x − 1/S)  where x = (S−1)/S × NTU_actual',
            substitution: `C_out = ${f(r.tanOut,2)} mg/L (N_actual = ${f(r.Zdesign/r.HTU,2)} NTU)`,
            result: `${f(r.tanOut,2)} mg/L`,
            status: r.tanOut <= params.targetTAN * 1.1 ? 'pass' : 'warn',
          },
          {
            label: 'Removal efficiency η',
            formula: 'η = (Cin − Cout) / Cin × 100 %',
            substitution: `η = (${params.inletTAN} − ${f(r.tanOut,2)}) / ${params.inletTAN} × 100 = ${f(r.removal,1)}%`,
            result: `${f(r.removal,1)}%`,
            status: r.removal >= 80 ? 'pass' : r.removal >= 60 ? 'warn' : 'fail',
          },
          {
            label: 'VTA (Venturi Tower Aerator) technology note',
            formula: 'VTA uses venturi jets to induce fine-bubble aeration without conventional packing media',
            substitution: 'Key advantages: no flooding, low maintenance, handles high-TDS/oily streams, hot-service capable',
            result: 'Suitable for 80–90°C hot stripping',
            status: 'pass',
            unit: 'Vendor sizing based on pilot/proprietary data',
          },
          {
            label: 'NTU method provides vendor design specification',
            formula: 'NTU specifies the mass-transfer duty; VTA vendor validates this against their contact system',
            substitution: `Required NTU = ${f(r.NTU,2)} → vendor confirms tower dimensions, air flow, HRT`,
            result: `${f(r.NTU,2)} NTU required`,
            status: 'pass',
            unit: '— vendor to submit Performance Guarantee data sheet',
          },
        ],
      },
    ];
  }, [params, result]);

  const passCount = result.validations.filter(v => v.pass).length;
  const failCount = result.validations.length - passCount;

  return (
    <div className="min-h-screen bg-rb-bg p-4 md:p-6 space-y-6">

      {/* ── Page Header ── */}
      <PageHeader
        icon={Wind}
        title="Ammonia Air Stripping Tower"
        subtitle="Packed-bed & VTA countercurrent NH₃ removal — full process simulation with hot stripping"
        iconColor="text-sky-400"
        actions={
          <div className="flex items-center gap-2">
            <span className={`text-xs font-bold px-2 py-1 rounded-lg ${
              failCount === 0 ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
              : 'bg-rose-500/10 text-rose-400 border border-rose-500/20'
            }`}>
              {passCount}/{result.validations.length} checks passed
            </span>
            <button
              onClick={handleLoadProject}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-sky-600/50 bg-sky-500/10 text-xs text-sky-300 hover:bg-sky-500/20 transition-colors"
            >
              <Layers className="w-3.5 h-3.5" /> Load 3S VTA Project
            </button>
            <button onClick={handleReset} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-700 text-xs text-slate-300 hover:bg-slate-800 transition-colors">
              <RotateCcw className="w-3.5 h-3.5" /> Reset
            </button>
            <button onClick={() => setShowReport(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-rb-brand text-white text-xs font-semibold hover:bg-rb-brand/90 transition-colors">
              <FileText className="w-3.5 h-3.5" /> Report
            </button>
          </div>
        }
      />

      {/* ── Banners ── */}
      {params.pH < 10 && (
        <div className="flex items-start gap-3 bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3">
          <AlertCircle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-300">
            <strong>Low pH:</strong> At pH {params.pH.toFixed(1)}, only {f(result.fNH3*100,1)}% of TAN is free NH₃. Raise to ≥ 10.0 for meaningful stripping.
          </p>
        </div>
      )}
      {result.moistureWarning && (
        <div className="flex items-start gap-3 bg-orange-500/10 border border-orange-500/30 rounded-xl px-4 py-3">
          <Thermometer className="w-4 h-4 text-orange-400 shrink-0 mt-0.5" />
          <p className="text-xs text-orange-300">
            <strong>Hot stripping mode ({params.temperature}°C):</strong> Water vapour pressure = {f(result.waterVapourPressureKPa,1)} kPa —
            air in tower is ~{f(result.steamContentPct,0)}% steam. Blower must be sized for wet-gas flow; moisture knockouts required.
            Arrhenius HTU model applied (linear model invalid above ~50°C).
          </p>
        </div>
      )}

      {/* ── Project Design Basis Panel (collapsible) ── */}
      <div className="rb-section overflow-hidden">
        <button
          onClick={() => setDbOpen(o => !o)}
          className="w-full flex items-center justify-between px-5 py-4 hover:bg-rb-elevated transition-colors"
        >
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-sky-400" />
            <span className="text-sm font-bold text-slate-200">Project Design Basis — 3S VTA (RCER-2025)</span>
            <span className="text-[11px] text-slate-500 ml-1">135 m³/hr · 6,500 → 780 → 100 mg/L NH₃</span>
          </div>
          {dbOpen ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
        </button>
        {dbOpen && (
          <div className="px-5 pb-5 border-t border-slate-800">
            <ProjectDesignBasisPanel />
          </div>
        )}
      </div>

      {/* ── Main 2-column layout ── */}
      <div className="grid grid-cols-1 xl:grid-cols-[420px_1fr] gap-6">

        {/* ─ LEFT: Inputs ─ */}
        <div className="space-y-4">

          {/* Water Quality */}
          <div className="rb-card p-5 space-y-4 rb-section">
            <h2 className="flex items-center gap-2 text-sm font-bold text-slate-200">
              <Droplets className="w-4 h-4 text-sky-400" /> Water Quality
            </h2>
            <SliderRow label="Flow rate Q (per unit)" value={params.flowRate} min={1} max={500} step={1} unit="m³/h"
              onChange={v => set('flowRate', v)} />
            <SliderRow label="Inlet TAN" value={params.inletTAN} min={1} max={12000} step={50} unit="mg/L"
              onChange={v => set('inletTAN', v)} />
            <SliderRow label="Target effluent TAN" value={params.targetTAN} min={1} max={5000} step={10} unit="mg/L"
              onChange={v => set('targetTAN', Math.min(v, params.inletTAN - 1))} />
            <SliderRow label="pH" value={params.pH} min={7} max={12} step={0.05} unit="—"
              onChange={v => set('pH', v)}
              warning={params.pH < 10 ? 'pH < 10 — stripping highly inefficient' : params.pH > 11.5 ? 'pH > 11.5 — excess caustic cost; check scaling risk' : undefined} />
            <SliderRow label="Temperature" value={params.temperature} min={5} max={90} step={1} unit="°C"
              onChange={v => set('temperature', v)}
              warning={params.temperature < 10 ? 'Below 10°C — very low Hcc' : params.temperature >= 70 ? 'Hot stripping — steam correction required' : undefined} />
          </div>

          {/* Tower Design */}
          <div className="rb-card p-5 space-y-4 rb-section">
            <h2 className="flex items-center gap-2 text-sm font-bold text-slate-200">
              <Settings className="w-4 h-4 text-sky-400" /> Tower Design
            </h2>
            <div>
              <label className="text-xs font-medium text-slate-300 block mb-1.5">Packing / contact type</label>
              <select
                value={params.packingType}
                onChange={e => set('packingType', e.target.value as PackingType)}
                className="w-full text-sm px-3 py-2 text-slate-100 focus:outline-none focus:border-sky-500 rb-inner-card-sm"
              >
                {(Object.entries(PACKING_HTU20) as [PackingType, { htu: number; label: string }][]).map(([k, v]) => (
                  <option key={k} value={k}>{v.label} — HTU₂₀ = {v.htu} m</option>
                ))}
              </select>
              <p className="text-[10px] text-slate-500 mt-1">{PACKING_HTU20[params.packingType].notes}</p>
            </div>
            <SliderRow label="Air-to-water ratio G/L" value={params.glRatio} min={50} max={600} step={10} unit="—"
              onChange={v => set('glRatio', v)}
              warning={params.glRatio > 500 ? 'G/L > 500 — high blower energy cost' : undefined} />
            <SliderRow label="Liquid loading rate QL" value={params.liquidLoading} min={5} max={40} step={1} unit="m³/m²·h"
              onChange={v => set('liquidLoading', v)}
              warning={params.liquidLoading > 40 ? 'Exceeds flood limit (40 m³/m²·h)' : undefined} />
            <SliderRow label="Safety factor" value={params.safetyFactor} min={1.1} max={1.5} step={0.05} unit="—"
              onChange={v => set('safetyFactor', v)} />
          </div>

          {/* pH Correction */}
          <div className="rb-card p-5 space-y-4 rb-section">
            <h2 className="flex items-center gap-2 text-sm font-bold text-slate-200">
              <Beaker className="w-4 h-4 text-sky-400" /> pH Correction
            </h2>
            <div>
              <label className="text-xs font-medium text-slate-300 block mb-1.5">pH reagent</label>
              <select value={params.reagent} onChange={e => set('reagent', e.target.value as ReagentType)}
                className="w-full text-sm px-3 py-2 text-slate-100 focus:outline-none focus:border-sky-500 rb-inner-card-sm">
                {(Object.entries(REAGENT_LABELS) as [ReagentType, string][]).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            {params.reagent !== 'none' && (
              <div className="flex items-center justify-between px-3 py-2.5 rb-inner-card-sm">
                <span className="text-xs text-slate-400">Estimated dose</span>
                <span className="text-sm font-bold text-sky-300">{f(result.pHDose,0)} g/m³</span>
              </div>
            )}
          </div>

          {/* Integration */}
          <div className="rb-card p-5 space-y-4 rb-section">
            <h2 className="flex items-center gap-2 text-sm font-bold text-slate-200">
              <Flame className="w-4 h-4 text-orange-400" /> Integration & Upset
            </h2>
            <SliderRow label="Flare back-pressure" value={params.flarePressureKPa} min={0} max={15} step={0.5} unit="kPa"
              onChange={v => set('flarePressureKPa', v)} />
            <SliderRow label="Urea concentration (upset)" value={params.ureaPpm} min={0} max={2000} step={50} unit="mg/L"
              onChange={v => set('ureaPpm', v)} />
            {result.ureaContrib > 0 && (
              <div className="flex items-center justify-between px-3 py-2 rb-inner-card-sm text-xs">
                <span className="text-slate-400">Urea → NH₃ at {params.temperature}°C</span>
                <span className="font-bold text-amber-300">+{f(result.ureaContrib,1)} mg/L  (upset: {f(result.upsetInletTAN,0)} mg/L)</span>
              </div>
            )}
          </div>

          {/* Feed Quality */}
          <div className="rb-card rb-section overflow-hidden">
            <button onClick={() => setShowFeed(o => !o)}
              className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-rb-elevated transition-colors">
              <h2 className="flex items-center gap-2 text-sm font-bold text-slate-200">
                <Activity className="w-4 h-4 text-sky-400" /> Feed Water Quality
              </h2>
              {showFeed ? <ChevronUp className="w-3.5 h-3.5 text-slate-500" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-500" />}
            </button>
            {showFeed && (
              <div className="px-5 pb-5 space-y-3 border-t border-slate-800">
                <p className="text-[11px] text-slate-500 pt-3">Feed characteristics after CPI/DNF pre-treatment. High TSS or free oil will foul packing. Alkalinity, hardness, silica and chloride drive caustic demand, scaling risk and material selection.</p>

                {/* A1 — Alkalinity & Caustic */}
                <p className="text-[10px] font-semibold text-sky-400 uppercase tracking-widest pt-1">A1 — Alkalinity / Caustic Demand</p>
                <SliderRow label="Alkalinity (as CaCO₃)" value={params.alkalinityMgL} min={0} max={2000} step={10} unit="mg/L"
                  onChange={v => set('alkalinityMgL', v)}
                  warning={params.alkalinityMgL > 800 ? 'High alkalinity — significantly increases NaOH demand' : undefined} />
                <SliderRow label="Free CO₂" value={params.freeCO2MgL} min={0} max={200} step={1} unit="mg/L"
                  onChange={v => set('freeCO2MgL', v)}
                  warning={params.freeCO2MgL > 50 ? 'High free CO₂ — adds to caustic demand during pH adjustment' : undefined} />
                {result.causticDoseTotal > 0 && (
                  <div className="grid grid-cols-3 gap-1">
                    {[
                      ['pH base',       `${f(result.pHDose, 0)} g/m³`],
                      ['+ Alkalinity',  `${f(result.causticDoseAlk, 0)} g/m³`],
                      ['+ CO₂',         `${f(result.causticDoseCO2, 0)} g/m³`],
                    ].map(([k, v]) => (
                      <div key={k} className="text-center px-2 py-1.5 rounded-lg rb-inner-card-sm">
                        <div className="text-[9px] text-slate-500">{k}</div>
                        <div className="text-xs font-mono font-bold text-sky-300">{v}</div>
                      </div>
                    ))}
                  </div>
                )}
                {result.causticDoseTotal > 0 && (
                  <div className="flex items-center justify-between px-3 py-2 rb-inner-card-sm text-xs">
                    <span className="text-slate-400 font-semibold">Total NaOH dose</span>
                    <span className="font-bold text-sky-300">{f(result.causticDoseTotal, 0)} g/m³</span>
                  </div>
                )}
                {params.freeCO2MgL > 0 && (
                  <p className="text-[10px] text-slate-500">
                    CO₂ ionisation at pH {params.pH}: <span className="text-slate-300">{f(result.co2ionised, 2)}%</span> —
                    at pH ≥ 10.5 essentially all free CO₂ is ionised; CO₂ increases caustic demand during pH-up step only.
                  </p>
                )}

                {/* A2 — Hardness / Scaling */}
                <p className="text-[10px] font-semibold text-sky-400 uppercase tracking-widest pt-1">A2/A3 — Hardness &amp; Silica (Scaling)</p>
                <SliderRow label="Total hardness (as CaCO₃)" value={params.totalHardnessMgL} min={0} max={3000} step={10} unit="mg/L"
                  onChange={v => set('totalHardnessMgL', v)} />
                <SliderRow label="Calcium hardness (as CaCO₃)" value={params.calciumHardnessMgL} min={0} max={2000} step={10} unit="mg/L"
                  onChange={v => set('calciumHardnessMgL', v)}
                  warning={params.calciumHardnessMgL > 400 ? 'High Ca hardness — elevated CaCO₃ scaling risk' : undefined} />
                <SliderRow label="Silica (SiO₂)" value={params.silicaMgL} min={0} max={300} step={1} unit="mg/L"
                  onChange={v => set('silicaMgL', v)}
                  warning={params.silicaMgL > 50 ? 'Silica > 50 mg/L at pH > 10 — risk of amorphous silica scale' : undefined} />

                {/* LSI result */}
                <div className={`flex items-center justify-between px-3 py-2 rounded-lg border text-xs ${
                  result.scalingRisk === 'none'     ? 'bg-emerald-500/5 border-emerald-500/20' :
                  result.scalingRisk === 'low'      ? 'bg-sky-500/5 border-sky-500/20' :
                  result.scalingRisk === 'moderate' ? 'bg-amber-500/5 border-amber-500/20' :
                  'bg-rose-500/5 border-rose-500/20'
                }`}>
                  <span className="text-slate-400">Langelier Saturation Index (LSI)</span>
                  <span className={`font-mono font-bold ${
                    result.scalingRisk === 'none' ? 'text-emerald-400' :
                    result.scalingRisk === 'low'  ? 'text-sky-400' :
                    result.scalingRisk === 'moderate' ? 'text-amber-400' : 'text-rose-400'
                  }`}>{f(result.lsi, 2)} — {result.scalingRisk.toUpperCase()}</span>
                </div>

                {/* A4 — Chloride / MoC */}
                <p className="text-[10px] font-semibold text-sky-400 uppercase tracking-widest pt-1">A4 — Chloride / Material of Construction</p>
                <SliderRow label="Chloride Cl⁻" value={params.chlorideMgL} min={0} max={5000} step={50} unit="mg/L"
                  onChange={v => set('chlorideMgL', v)}
                  warning={params.chlorideMgL > 500 && params.temperature > 60 ? 'Cl⁻ > 500 mg/L at T > 60°C — upgrade to Duplex 2205 minimum' : params.chlorideMgL > 2000 ? 'Cl⁻ > 2000 mg/L — Super-duplex 2507 required' : undefined} />
                <div className="px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700 text-xs space-y-0.5">
                  <div className="flex items-center gap-1.5">
                    <ShieldCheck className="w-3.5 h-3.5 text-sky-400 shrink-0" />
                    <span className="font-semibold text-slate-200">{result.mocCode}</span>
                  </div>
                  <p className="text-[10px] text-slate-400">{result.mocRationale}</p>
                </div>

                {/* A8 — Direct NH₃ upset */}
                <p className="text-[10px] font-semibold text-sky-400 uppercase tracking-widest pt-1">A8 — Direct NH₃ Upset Load</p>
                <SliderRow label="Peak NH₃ at inlet (direct)" value={params.upsetNH3Direct} min={0} max={15000} step={100} unit="ppm"
                  onChange={v => set('upsetNH3Direct', v)}
                  warning={params.upsetNH3Direct > 5000 ? 'Extreme upset — validate stripper and flare capacity at this load' : params.upsetNH3Direct > 1000 ? 'Significant upset NH₃ — size blower and flare for peak load' : undefined} />

                {/* Divider — original fouling params */}
                <p className="text-[10px] font-semibold text-sky-400 uppercase tracking-widest pt-1">Pre-treatment / Fouling Indicators</p>
                <SliderRow label="COD" value={params.codFeed} min={0} max={2000} step={10} unit="mg/L" onChange={v => set('codFeed', v)} />
                <SliderRow label="TDS" value={params.tdsFeed} min={0} max={15000} step={100} unit="mg/L" onChange={v => set('tdsFeed', v)} />
                <SliderRow label="TSS" value={params.tssFeed} min={0} max={200} step={1} unit="mg/L" onChange={v => set('tssFeed', v)}
                  warning={params.tssFeed > 50 ? 'TSS > 50 mg/L — risk of packing fouling' : undefined} />
                <SliderRow label="Free Oil" value={params.freeOilFeed} min={0} max={100} step={1} unit="mg/L" onChange={v => set('freeOilFeed', v)}
                  warning={params.freeOilFeed > 10 ? 'Free Oil > 10 mg/L — risk of foaming/fouling; improve CPI/DNF' : undefined} />
                <SliderRow label="BOD₅" value={params.bodFeed} min={0} max={1000} step={5} unit="mg/L" onChange={v => set('bodFeed', v)} />
              </div>
            )}
          </div>
        </div>

        {/* ─ RIGHT: Results ─ */}
        <div className="space-y-5">

          {/* Summary Metrics Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            <MetricCard label="Free NH₃ fraction"   value={f(result.fNH3*100,1)}     unit="%" sub={`pKa = ${f(result.pKa,3)}`} />
            <MetricCard label="Henry Hcc(T)"         value={f(result.hcc,5)}           sub="Staudinger & Roberts" />
            <MetricCard label="H_eff"                value={f(result.hEff,5)}           sub="×f_NH₃" />
            <MetricCard label="Stripping factor S"   value={f(result.S,2)}
              warn={result.S < 1.5} accent={result.S >= 10}
              sub={result.S >= 10 ? 'Excellent (hot)' : result.S >= 5 ? 'Good' : result.S >= 1.5 ? 'Marginal' : 'Poor'} />
            <MetricCard label="NTU"                  value={f(result.NTU,2)}           sub="transfer units" warn={result.NTU > 20 || result.NTU < 2} />
            <MetricCard label="HTU (Arrhenius)"      value={f(result.HTU,3)}           unit="m" sub={`θ=1.024, ref ${htu20}m@20°C`} />
            <MetricCard label="Tower height Z"       value={f(result.Z,3)}             unit="m" sub="theoretical" />
            <MetricCard label="Design height"        value={f(result.Zdesign,2)}       unit="m"
              warn={result.Zdesign > 15} accent={result.Zdesign <= 5}
              sub={result.minHeightApplied ? 'HRT min applied' : `×${params.safetyFactor} SF`} />
            <MetricCard label="Diameter D"           value={f(result.D,2)}             unit="m" sub={`A = ${f(result.A,2)} m²`} />
            <MetricCard label="Air flow Q_air"       value={f(result.Qair,0)}          unit="m³/h" sub={`G/L = ${params.glRatio}`} />
            <MetricCard label="Blower pressure"      value={f(result.blowerPressure,2)} unit="kPa"
              sub={`incl. ${params.flarePressureKPa} kPa flare`} />
            <MetricCard label="Blower power"         value={f(result.blowerPower,2)}   unit="kW" sub="per unit (η=70%)" />
            <MetricCard label="Steam content"        value={f(result.steamContentPct,1)} unit="%"
              warn={result.steamContentPct > 40} sub={`Pv = ${f(result.waterVapourPressureKPa,1)} kPa`} />
            <MetricCard label="Effluent TAN"         value={f(result.tanOut,2)}        unit="mg/L"
              accent={result.tanOut <= params.targetTAN * 1.05}
              warn={result.tanOut > params.targetTAN * 1.2}
              sub={`target: ${params.targetTAN} mg/L`} />
            <MetricCard label="Removal efficiency"   value={f(result.removal,1)}       unit="%"
              accent={result.removal >= 80} warn={result.removal < 60} sub="TAN" />
            <MetricCard label="DO increase"          value={`+${f(result.doIncrease,2)}`} unit="mg/L" sub="re-aeration" />
          </div>

          {/* Validation checks */}
          <div className="p-5 rb-section">
            <h2 className="flex items-center gap-2 text-sm font-bold text-slate-200 mb-3">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" /> Design Validation
              <span className={`ml-auto text-xs font-bold px-2 py-0.5 rounded ${
                failCount === 0 ? 'bg-emerald-500/10 text-emerald-400' : 'bg-rose-500/10 text-rose-400'
              }`}>{passCount}/{result.validations.length}</span>
            </h2>
            <div className="space-y-1.5">
              {result.validations.map((v, i) => (
                <div key={i} className={`flex items-center justify-between rounded-lg px-3 py-2 text-xs border ${
                  v.pass ? 'bg-emerald-500/5 border-emerald-500/15' : 'bg-rose-500/5 border-rose-500/20'
                }`}>
                  <div className="flex items-center gap-2">
                    {v.pass ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" /> : <AlertCircle className="w-3.5 h-3.5 text-rose-400 shrink-0" />}
                    <span className="text-slate-300">{v.label}</span>
                  </div>
                  <span className={`font-mono font-bold ${v.pass ? 'text-emerald-400' : 'text-rose-400'}`}>{v.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Temperature sensitivity table */}
          <div className="p-5 rb-section">
            <h2 className="flex items-center gap-2 text-sm font-bold text-slate-200 mb-3">
              <Thermometer className="w-4 h-4 text-orange-400" /> Temperature Sensitivity (Arrhenius HTU)
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="border-b border-slate-700">
                    {['T (°C)', 'Hcc', 'S', 'NTU', 'HTU (m)', 'Z_design (m)', 'η (%)'].map(h => (
                      <th key={h} className="text-left py-2 px-2 text-slate-400 font-medium whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[15, 25, 40, 60, 70, 80, 85, 90]
                    .map(t => {
                      const r = calcStripper({ ...params, temperature: t });
                      const isCurrent = t === params.temperature;
                      return (
                        <tr key={t} className={`border-b border-slate-800/60 ${isCurrent ? 'bg-sky-500/5' : 'hover:bg-slate-800/30'}`}>
                          <td className={`py-2 px-2 font-mono ${isCurrent ? 'text-sky-300 font-bold' : 'text-slate-300'}`}>{t}{isCurrent ? ' ←' : ''}</td>
                          <td className="py-2 px-2 font-mono text-slate-300">{f(r.hcc,4)}</td>
                          <td className={`py-2 px-2 font-mono ${r.S >= 10 ? 'text-emerald-400' : r.S >= 1.5 ? 'text-amber-400' : 'text-rose-400'}`}>{f(r.S,2)}</td>
                          <td className="py-2 px-2 font-mono text-slate-300">{f(r.NTU,2)}</td>
                          <td className="py-2 px-2 font-mono text-slate-300">{f(r.HTU,4)}</td>
                          <td className={`py-2 px-2 font-mono ${r.Zdesign <= 5 ? 'text-emerald-400' : r.Zdesign <= 15 ? 'text-slate-300' : 'text-rose-400'}`}>{f(r.Zdesign,2)}{r.minHeightApplied ? '*' : ''}</td>
                          <td className={`py-2 px-2 font-mono ${r.removal >= 80 ? 'text-emerald-400' : r.removal >= 60 ? 'text-amber-400' : 'text-rose-400'}`}>{f(r.removal,1)}</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
              <p className="text-[10px] text-slate-500 mt-1.5">* Minimum hydraulic height applied (VTA: 3.0 m / packed: 2.0 m)</p>
            </div>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="p-5 rb-section">
              <h2 className="flex items-center gap-2 text-sm font-bold text-slate-200 mb-4">
                <BarChart3 className="w-4 h-4 text-sky-400" /> Removal Efficiency vs pH
              </h2>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={phChartData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="pH" tick={{ fontSize: 10, fill: '#94a3b8' }} label={{ value: 'pH', position: 'insideBottom', offset: -2, fill: '#94a3b8', fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} domain={[0, 100]} unit="%" />
                  <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8, fontSize: 11 }} labelStyle={{ color: '#94a3b8' }} />
                  <ReferenceLine x={params.pH} stroke="#0ea5e9" strokeDasharray="4 3" label={{ value: `pH ${params.pH}`, fill: '#0ea5e9', fontSize: 9 }} />
                  <ReferenceLine x={10.5} stroke="#f59e0b" strokeDasharray="4 3" label={{ value: 'pH 10.5', fill: '#f59e0b', fontSize: 9 }} />
                  <Line type="monotone" dataKey="removal" stroke="#0ea5e9" strokeWidth={2} dot={false} name="Removal %" />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="p-5 rb-section">
              <h2 className="flex items-center gap-2 text-sm font-bold text-slate-200 mb-4">
                <Zap className="w-4 h-4 text-sky-400" /> Stripping Factor vs Temperature
              </h2>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={tempChartData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="temp" tick={{ fontSize: 10, fill: '#94a3b8' }} label={{ value: 'T (°C)', position: 'insideBottom', offset: -2, fill: '#94a3b8', fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} />
                  <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8, fontSize: 11 }} labelStyle={{ color: '#94a3b8' }} />
                  <ReferenceLine x={params.temperature} stroke="#0ea5e9" strokeDasharray="4 3" label={{ value: `${params.temperature}°C`, fill: '#0ea5e9', fontSize: 9 }} />
                  <ReferenceLine x={80} stroke="#f97316" strokeDasharray="4 3" label={{ value: '80°C', fill: '#f97316', fontSize: 9 }} />
                  <Line type="monotone" dataKey="S" stroke="#f97316" strokeWidth={2} dot={false} name="Stripping Factor S" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* DO note */}
          <div className="flex items-start gap-3 rounded-xl bg-emerald-500/5 border border-emerald-500/20 px-4 py-3">
            <Info className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
            <p className="text-xs text-emerald-300">
              <strong>Re-aeration effect:</strong> Air stripping at G/L = {params.glRatio} increases effluent DO by approximately{' '}
              <strong>+{f(result.doIncrease,2)} mg/L</strong>. This benefits downstream biological treatment but may require
              pH correction before discharge (stripping raises effluent pH above process setpoint).
            </p>
          </div>

          {/* Science Accordion */}
          <div className="overflow-hidden rb-section">
            <button onClick={() => setSciOpen(o => !o)}
              className="w-full flex items-center justify-between px-5 py-4 hover:bg-rb-elevated transition-colors">
              <div className="flex items-center gap-2">
                <FlaskConical className="w-4 h-4 text-sky-400" />
                <span className="text-sm font-bold text-slate-200">Full Calculation Breakdown (Science & Physics)</span>
              </div>
              {sciOpen ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
            </button>
            {sciOpen && (
              <div className="px-5 pb-5 border-t border-slate-800">
                <ScienceAccordion sections={scienceSections} defaultOpen={false} />
              </div>
            )}
          </div>

        </div>
      </div>

      {/* ── Two-Stage Series Performance (full-width) ── */}
      <SeriesPerformancePanel series={seriesResult} params={params} />

      {/* ── Clarification Register (A–D) ── */}
      <ClarificationRegisterPanel params={params} result={result} />

      {/* ── Performance Guarantee ── */}
      <PerformanceGuaranteePanel params={params} result={result} series={seriesResult} />

      {/* Report modal */}
      {showReport && <ReportModal params={params} result={result} onClose={() => setShowReport(false)} />}
    </div>
  );
}
